<template>
    <div class="row" v-if="failure">
        <div class="col s6 offset-s3 center-align">
            <h5>
                There was a problem processing your file.  We will return to you to the dashboard in a few seconds.  If this consumed one of your credits, please contact us with details and we will correct this.
            </h5>
        </div>
    </div>
    <div v-else>
        <div v-if="fileLoading" style="height:100%;text-align:center;">
            <h4>Loading</h4>
            <div class="preloader-wrapper small active">
                <div class="spinner-layer">
                    <div class="circle-clipper left">
                        <div class="circle"></div>
                    </div>
                    <div class="gap-patch">
                        <div class="circle"></div>
                    </div>
                    <div class="circle-clipper right">
                        <div class="circle"></div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="container">
            <div class="row">
                <div class="col s12">
                    <div class="center-align startjobor">
                        <h4 class="bold title">
                            {{nestedMode ? 'Create nested job' : 'Create job for'}}
                        </h4>
                        <h4 class="bold file-name" v-if="!nestedMode">{{file.name}}:</h4>
                    </div>
                </div>
                <div class="col s10 offset-s1 init-settings">
                    <div class="col s12 l6 center-align settings-block">
                        <h5 class="bold block-title">Choose materials:</h5>
                        <dropdown :visible="materialvisible" :position="position" @clickout="materialvisible = false">
                            <span class="link btn a3d" @click="materialvisible = true">{{ materials[material - 1].name }}<span
                                    v-if="materials[material - 1].trademark">&trade;</span> <i
                                class="fa fa-angle-down" aria-hidden="true"></i></span>
                            <div slot="dropdown" class="dialog no-padding">
                                <aside class="menu">
                                    <ul class="menu-list">
                                        <li v-for="(material, index) in materials" :key="material.name">
                                            <a class="a3d btn inv" @click="materialChoice(index)">
                                                <i class="material-icons a3d color"
                                                   v-if="index > predefinedMaterials - 1 && index !== materials.length - 1"
                                                   @click.stop="removeMaterial(index)">
                                                    clear
                                                </i>
                                                {{material.name}}<span v-if="material.trademark">&trade;</span>
                                            </a>
                                        </li>
                                    </ul>
                                </aside>
                            </div>
                        </dropdown>
                        <div class="alert" v-if="!isValidMaterial">Material is unvalidated</div>
                    </div>
                    <div class="col s12 l6 center-align settings-block">
                        <h5 class="bold block-title">Choose printer:</h5>
                        <dropdown :visible="printervisible" :position="position" @clickout="printervisible = false">
                        <span class="link btn a3d" @click="printervisible = true">{{ printers[printer - 1].name }} <i
                                class="fa fa-angle-down" aria-hidden="true"></i></span>
                            <div slot="dropdown" class="dialog no-padding">
                                <aside class="menu">
                                    <ul class="menu-list">
                                        <li v-for="(printer, index) in printers" :key="printer.name">
                                            <a class="a3d btn inv" @click="printerChoice(index)">
                                                <i class="material-icons a3d color"
                                                   v-if="index > predefinedPrinters - 1 && index !== printers.length - 1"
                                                   @click.stop="removePrinter(index)">
                                                    clear
                                                </i>
                                                {{printer.name}}
                                            </a>
                                        </li>
                                    </ul>
                                </aside>
                            </div>
                        </dropdown>
                        <div class="alert" v-if="!printers[printer - 1].valid">Physical validation is still pending</div>
                        <div class="alert" v-if="printer > predefinedPrinters">Printer is unvalidated</div>
                    </div>
                </div>
            </div>
            <template v-if="!nestedMode">
                <div class="blocks-wrp">
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the units you selected when creating your .stl file (usually mm or cm):
                            </div>
                            <div class="block-value">
                                <dropdown :visible="unitvisible" :position="position" @clickout="unitvisible = false">
                                    <span class="link btn a3d" @click="unitvisible = true">{{ units[files[0].unit - 1].name }}
                                        <i class="fa fa-angle-down" aria-hidden="true"></i>
                                    </span>
                                    <div slot="dropdown" class="dialog no-padding">
                                        <aside class="menu">
                                            <ul class="menu-list">
                                                <li v-for="(unit, index) in units" :key="unit.name">
                                                    <a class="a3d btn inv" @click="unitChoice(index)">{{unit.name}}</a>
                                                </li>
                                            </ul>
                                        </aside>
                                    </div>
                                </dropdown>
                            </div>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Input thickness of each powder layer (must be consistent with the layer thickness set in your printer):</div>
                            <div class="block-value">
                                <input type="text" id="thicknessfield" v-model="thickness.current"
                                       @change="verifyNumPositive(thickness)"
                                       class="validate">
                                <div class="unit">µm</div>
                            </div>
                            <h6 class="error" v-if="thickness.invalid">Please input a valid thickness.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the scaling factor you want to apply when printing the file, relative to your .stl file (example: if you want to print at 150% the dimensions of your .stl file, you would enter 1.50):
                            </div>
                            <div class="block-value">
                                <input type="text" id="scalefield" v-model="files[0].scale.current"
                                       @change="verifyNumPositive(files[0].scale)"
                                       class="validate">
                            </div>
                            <h6 class="error" v-if="files[0].scale.invalid">Please input a valid scale.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the distance between the part and the build platform. (Z-offset)
                            </div>
                            <div class="block-value">
                                <input type="text" id="hsubfield" v-model="files[0].hsub.current"
                                       @change="verifyNum(files[0].hsub)"
                                       :disabled="isWithoutSupport"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="files[0].hsub.invalid">Please input a valid distance.</h6>
                            <div class="block-note">
                                <h6 class="center-align">
                                    (If the distance is 0, the part will be built directly from the build platform.)
                                </h6>
                            </div>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the temperature of the build platform.
                            </div>
                            <div class="block-value">
                                <input type="text" id="tplatefield" v-model="tplate.current"
                                       @change="verifyNum(tplate)"
                                       class="validate">
                                <div class="unit">&deg;C</div>
                            </div>
                            <h6 class="error" v-if="tplate.invalid">Please input a valid temperature.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the build plate thickness.
                            </div>
                            <div class="block-note left-align">
                              A difference of +/- 20% or more is material to the simulation.
                            </div>
                            <div class="block-value">
                                <input type="text" id="hbuildplatefield" v-model="H_buildPlate.current"
                                       @change="verifyNumPositive(H_buildPlate)"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="H_buildPlate.invalid">Please input a valid thickness.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Input your suggested element size:</div>
                            <div class="block-value">
                                <input type="text"
                                       id="elementfield"
                                       v-model="element.current"
                                       @change="verifyNumPositive(element)"
                                       @input="calculateElementSizeReset()"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="element.invalid">Please input a valid size.</h6>
                            <div class="block-note">
                                <h6 class="center-align">
                                    (We suggest 2 mm. A smaller element size will result in a more accurate optimization, but a longer processing time.)
                                </h6>
                            </div>
                            <button class="a3d btn multiline"
                                    v-if="!checkElementSizeResult || (checkElementSizeResult && checkElementSizeError)"
                                    @click="calculateElementSize()"
                                    :disabled="checkElementSizeProcess">
                                Confirm that your element size is not too small
                            </button>
                            <div v-if="checkElementSizeProcess" class="progress">
                                <div class="indeterminate"></div>
                            </div>
                            <div class="block-note bold" v-if="checkElementSizeResult" :class="{'error': checkElementSizeError}">
                                <h6 class="center-align">
                                    {{checkElementSizeResult}}
                                </h6>
                            </div>
                        </div>
                    </div>
                    <div class="divider-block center-align" v-if="!this.showAdvanced">
                        <button class="a3d btn" @click="confirmAdvanced()">
                            Advanced Options
                        </button>
                    </div>
                    <h5 class="divider-block center-align block-title bold" v-else>
                        Advanced Options
                    </h5>
                </div>
                <div class="blocks-wrp" v-if="this.showAdvanced">
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Infill laser scan speed:</div>
                            <div class="block-value">
                                <input type="text" id="speedfield" v-model="speed.current" @change="verifyNumPositive(speed)"
                                       class="validate">
                                <div class="unit">mm / s</div>
                            </div>
                            <h6 class="error" v-if="speed.invalid">Please input a valid speed.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Infill laser beam diameter:</div>
                            <div class="block-value">
                                <input type="text" id="diameterfield" v-model="diameter.current" @change="verifyNumPositive(diameter)"
                                       class="validate">
                                <div class="unit">µm</div>
                            </div>
                            <h6 class="error" v-if="diameter.invalid">Please input a valid diameter.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Infill laser power:</div>
                            <div class="block-value">
                                <input type="text" id="powerfield" v-model="power.current" @change="verifyNumPositive(power)"
                                       class="validate">
                                <div class="unit">W</div>
                            </div>
                            <h6 class="error" v-if="power.invalid">Please input a valid power.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                              Number of lasers employed for this build
                            </div>
                            <div class="block-note left-align">
                              (only impacts build time calculation)
                            </div>
                            <div class="block-value">
                                <input type="text" id="nlaserfield" v-model="nlaser.current" @change="verifyNumPositive(nlaser)"
                                       class="validate">
                            </div>
                            <h6 class="error" v-if="nlaser.invalid">Please input a valid number of lasers.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Laser scanning speed for support structures:</div>
                            <div class="block-value">
                                <input type="text" id="vlaserSupp" v-model="vlaserSupp.current" @change="verifyNumPositive(vlaserSupp)"
                                       class="validate">
                                <div class="unit">mm / s</div>
                            </div>
                            <h6 class="error" v-if="vlaserSupp.invalid">Please input a valid laser scanning speed.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Hatch distance:</div>
                            <div class="block-value">
                                <input type="text" id="hatch" v-model="hatch.current" @change="verifyNumPositive(hatch)"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="hatch.invalid">Please input a valid hatch distance.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Recoater Crash Tolerance:</div>
                            <div class="block-note left-align">
                              0.4 mm is the tolerance of a stiff recoater blade. Flexible recoater blade materials will allow for more
                              z-direction displacement. Please indicate the tolerance of the recoater material employed for this build.
                            </div>
                            <div class="block-value">
                                <input type="text" id="crashtolerancefield" v-model="CrashTolerance.current" @change="verifyNumPositive(CrashTolerance)"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="CrashTolerance.invalid">Please input a valid recoater crash tolerance.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the critical angle for generating support structures (in degrees):
                            </div>
                            <div class="block-value">
                                <input type="text" id="critical_anglefield" v-model="files[0].critical_angle.current"
                                       @change="verifyNum(files[0].critical_angle)" class="validate">
                            </div>
                            <h6 class="error" v-if="files[0].critical_angle.invalid">Please input a valid angle.</h6>
                            <div class="block-note">
                                <h6 class="center-align">
                                    Surfaces with angles relative to the build plate smaller than this angle will be supported
                                </h6>
                            </div>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter material dependent scaling defined in your machine:
                            </div>
                            <div class="block-value">
                                <div class="unit label">X-axis</div>
                                <input type="text" id="scalingX_material" v-model="scalingX_material.current"
                                       @change="verifyNumPositiveFromZero(scalingX_material)" class="validate">
                                <div class="unit">%</div>
                            </div>
                            <h6 class="error" v-if="scalingX_material.invalid">Please input a valid value.</h6>
                            <div class="block-value">
                                <div class="unit label">Y-axis</div>
                                <input type="text" id="scalingY_material" v-model="scalingY_material.current"
                                       @change="verifyNumPositiveFromZero(scalingY_material)" class="validate">
                                <div class="unit">%</div>
                            </div>
                            <h6 class="error" v-if="scalingY_material.invalid">Please input a valid value.</h6>
                            <div class="block-note">
                                <h6 class="center-align">
                                    Material dependent scaling is a global addition or subtraction of material in the indicated plane.
                                </h6>
                            </div>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Please enter parameters for distortion compensation:</div>
                            <div class="col s12 center-align">
                                <input type="checkbox" class="filled-in" id="isCompensate"
                                      v-model="files[0].isCompensate"/>
                                <label for="isCompensate">Create an additional job result that includes design modifications that compensate for thermal distortion (remeshing is required)</label>
                            </div>
                            <div class="block-value">
                                <div class="unit label">Scaling Factor</div>
                                <input type="text" id="compensationScale" v-model="compensationScale.current" @change="verifyNumPositive(compensationScale)" class="validate">
                            </div>
                            <div class="block-note">
                              <h6 class="center-align">
                                higher value gives more compensation
                              </h6>
                            </div>
                            <h6 class="error" v-if="compensationScale.invalid">Please input a valid scale.</h6>
                            <div class="block-value">
                                <div class="unit label">Mesh envelope size</div>
                                <input type="text" id="tetraEpsField" v-model="tetraEps.current" @change="verifyNumPositive(tetraEps)"
                                       class="validate">
                            </div>
                            <h6 class="error" v-if="tetraEps.invalid">Please input a valid size.</h6>
                            <div class="block-value">
                                <div class="unit label">Tetra mesh size</div>
                                <input type="text" id="tetraMeshSizeField" v-model="tetraMeshSize.current" @change="verifyNumPositive(tetraMeshSize)"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="tetraMeshSize.invalid">Please input a valid size.</h6>
                        </div>
                    </div>

                    <h5 class="divider-block center-align block-title bold">
                        Support Structure
                    </h5>
                    <div class="center-align block-wrp block-wrp-100">
                      <input type="checkbox" class="filled-in" id="isWithoutSupport"
                             v-model="isWithoutSupport"/>
                      <label for="isWithoutSupport">Lay parts directly on build platform without any support</label>
                    </div>
                    <div class="center-align block-wrp block-wrp-100" v-if="!isWithoutSupport">
                        <div class="block-input">
                            <input id="uploadsupports" @change="uploadSupports" accept=".stl" type="file" multiple>
                            <button class="a3d btn" @click="selectSupportsFile">Upload supports STL</button>
                            <div class="block-buttons" v-if="Object.keys(fileSupports) > 0">
                                <span class="bold">Supports units:</span>
                                <dropdown :visible="unitsuppvisible" :position="position" @clickout="unitsuppvisible = false">
                                    <span class="link btn a3d" @click="unitsuppvisible = true">{{ units_supp[files[0].unit_supp].name }}
                                        <i class="fa fa-angle-down" aria-hidden="true"></i>
                                    </span>
                                    <div slot="dropdown" class="dialog no-padding">
                                        <aside class="menu">
                                            <ul class="menu-list">
                                                <li v-for="(unit, index) in units_supp" :key="unit.name">
                                                    <a class="a3d btn inv" @click="unitSuppChoice(index)">{{unit.name}}</a>
                                                </li>
                                            </ul>
                                        </aside>
                                    </div>
                                </dropdown>
                            </div>
                            <div class="block-support" v-for="fileSupport in Object.keys(fileSupports)" :key="fileSupport">
                                <div class="bold upload-supports">{{fileSupports[fileSupport].name}}</div>
                                <button class="a3d btn inv" @click="removeSupportsFile(fileSupport)">Remove</button>
                            </div>
                            <div v-if="uploadSupportsMessage">{{uploadSupportsMessage}}</div>
                        </div>
                    </div>
                    <template v-if="!(Object.keys(fileSupports).length > 0 || isWithoutSupport)">
                        <div class="center-align block-wrp with-image">
                          <div class="block-image">
                            <img src="../assets/support-wl.png" alt="Support width and length"/>
                          </div>
                          <div class="block-input">
                              <div class="bold block-description">
                                  Individual support width and length (WL<sub>0</sub>)
                              </div>
                              <div class="block-value">
                                  <input type="text" id="suppSpacing_xyfield" v-model="files[0].suppSpacing_xy.current"
                                        @change="verifyNumPositive(files[0].suppSpacing_xy)"
                                        class="validate">
                                  <div class="unit">mm</div>
                              </div>
                              <h6 class="error" v-if="files[0].suppSpacing_xy.invalid">Please input a valid distance.</h6>
                              <div class="block-note">
                                  <h6 class="center-align">
                                      (The default is 1.0 mm)
                                  </h6>
                              </div>
                          </div>
                        </div>
                        <div class="center-align block-wrp">
                          <div class="block-input">
                              <div class="bold block-description">
                                  Please enter the size of the desired support at boundary.
                              </div>
                              <div class="block-value">
                                  <input type="text" id="supp_size_boundaryfield" v-model="files[0].supp_size_boundary.current"
                                        @change="verifyNumPositive(files[0].supp_size_boundary)"
                                        class="validate">
                                  <div class="unit">mm</div>
                              </div>
                              <h6 class="error" v-if="files[0].supp_size_boundary.invalid">Please input a valid distance.</h6>
                              <div class="block-note">
                                  <h6 class="center-align">
                                      (The default is 0.6 mm)
                                  </h6>
                              </div>
                          </div>
                        </div>
                        <div class="center-align block-wrp block-wrp__flex">
                          <div class="block-input block-input__half">
                              <div class="bold block-description">
                                  Support structure hatch distance
                              </div>
                              <div class="block-value">
                                  <input type="text" id="suppHatchSizefield" v-model="files[0].suppHatchSize.current"
                                        @change="verifyNumPositive(files[0].suppHatchSize)"
                                        class="validate">
                                  <div class="unit">mm</div>
                              </div>
                              <h6 class="error" v-if="files[0].suppHatchSize.invalid">Please input a valid distance.</h6>
                              <div class="block-note">
                                  <h6 class="center-align">
                                      (The default is 3.5 mm)
                                  </h6>
                              </div>
                          </div>
                          <div class="block-input block-input__half">
                            <div class="left-align">
                              <input type="checkbox" class="filled-in" id="isStrengthSupp" v-model="isStrengthSupp"/>
                              <label for="isStrengthSupp" class="bold block-description">Use strengthened supports where distortion is highest?</label>
                            </div>
                          </div>
                        </div>
                        <div class="center-align block-wrp with-image">
                          <div class="block-image">
                            <img src="../assets/support-fs.png" alt="Critical angle"/>
                          </div>
                          <div class="block-input">
                            <div class="bold block-descripton">
                              Critical angle evaluated on:
                            </div>
                            <div class="support-checkboxes">
                              <div class="left-align">
                                <input type="checkbox" class="filled-in" id="isEvalFacet"
                                      v-model="isEvalFacet"/>
                                <label for="isEvalFacet">STL faces</label>
                              </div>
                              <div class="left-align">
                                <input type="checkbox" class="filled-in" id="isEvalVertex"
                                      v-model="isEvalVertex"/>
                                <label for="isEvalVertex">STL vertices</label>
                              </div>
                              <div class="left-align">
                                <input type="checkbox" class="filled-in" id="isEvalEdge"
                                      v-model="isEvalEdge"/>
                                <label for="isEvalEdge">STL edges</label>
                              </div>
                              <div class="left-align">
                                <input type="checkbox" class="filled-in" id="isFragmentation"
                                      v-model="isFragmentation"/>
                                <label for="isFragmentation">Divide support blocks into fragments for easier removal</label>
                              </div>
                              <div class="block-input">
                                <div class="bold block-description">Fragmentation size (S<sub>0</sub>):</div>
                                <div class="block-value">
                                    <input type="text" id="fragmentationsizefield" v-model="fragmentationSize.current" @change="verifyNumPositive(fragmentationSize)"
                                          class="validate">
                                    <div class="unit">mm</div>
                                </div>
                                <h6 class="error" v-if="fragmentationSize.invalid">Please input a valid fragmentation size.</h6>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="center-align block-wrp block-wrp-33">
                            <div class="block-input block-support-input">
                                <div class="bold block-description">
                                    Change the following parameters to change the support geometry:
                                </div>
                                <!-- Width Top -->
                                <div class="block-value">
                                    <div class="field-name">W<sub>t</sub></div>
                                    <input type="number"
                                           v-model="supportParams.wt.current"
                                           @change="verifySuppRange('wt')"
                                           :min="suppRanges.wt.min"
                                           :max="suppRanges.wt.max">
                                    <div class="unit">%</div>
                                </div>
                                <h6 class="error" v-if="supportParams.wt.invalid">{{supportErrorMessage('wt')}}</h6>
                                <!-- Penetrate Top -->
                                <div class="block-value">
                                    <div class="field-name">P<sub>t</sub></div>
                                    <input type="number"
                                           v-model="supportParams.pt.current"
                                           @change="verifySuppRange('pt')"
                                           :min="suppRanges.pt.min"
                                           :max="suppRanges.pt.max"
                                           :step="suppRanges.pt.step">
                                    <div class="unit">mm</div>
                                </div>
                                <h6 class="error" v-if="supportParams.pt.invalid">{{supportErrorMessage('pt')}}</h6>
                                <!-- Trapecia Top -->
                                <div class="block-value">
                                    <div class="field-name">T<sub>t</sub></div>
                                    <input type="number"
                                           v-model="supportParams.tt.current"
                                           @change="verifySuppRange('tt')"
                                           :min="suppRanges.tt.min"
                                           :max="suppRanges.tt.max"
                                           :step="suppRanges.tt.step">
                                    <div class="unit">mm</div>
                                </div>
                                <h6 class="error" v-if="supportParams.tt.invalid">{{supportErrorMessage('tt')}}</h6>
                                <!-- OnPart Top -->
                                <div class="block-value">
                                    <div class="field-name">O<sub>t</sub></div>
                                    <input type="number"
                                           v-model="supportParams.ot.current"
                                           @change="verifySuppRange('ot')"
                                           :min="suppRanges.ot.min"
                                           :max="suppRanges.ot.max"
                                           :step="suppRanges.ot.step">
                                    <div class="unit">mm</div>
                                </div>
                                <h6 class="error" v-if="supportParams.ot.invalid">{{supportErrorMessage('ot')}}</h6>
                                <!-- Width Bot -->
                                <div class="block-value">
                                    <div class="field-name">W<sub>b</sub></div>
                                    <input type="number"
                                           v-model="supportParams.wb.current"
                                           @change="verifySuppRange('wb')"
                                           :min="suppRanges.wb.min"
                                           :max="suppRanges.wb.max">
                                    <div class="unit">%</div>
                                </div>
                                <h6 class="error" v-if="supportParams.wb.invalid">{{supportErrorMessage('wb')}}</h6>
                                <!-- Penetrate Bot -->
                                <div class="block-value">
                                    <div class="field-name">P<sub>b</sub></div>
                                    <input type="number"
                                           v-model="supportParams.pb.current"
                                           @change="verifySuppRange('pb')"
                                           :min="suppRanges.pb.min"
                                           :max="suppRanges.pb.max"
                                           :step="suppRanges.pb.step">
                                    <div class="unit">mm</div>
                                </div>
                                <h6 class="error" v-if="supportParams.pb.invalid">{{supportErrorMessage('pb')}}</h6>
                                <!-- Trapecia Bot -->
                                <div class="block-value">
                                    <div class="field-name">T<sub>b</sub></div>
                                    <input type="number"
                                           v-model="supportParams.tb.current"
                                           @change="verifySuppRange('tb')"
                                           :min="suppRanges.tb.min"
                                           :max="suppRanges.tb.max"
                                           :step="suppRanges.tb.step">
                                    <div class="unit">mm</div>
                                </div>
                                <h6 class="error" v-if="supportParams.tb.invalid">{{supportErrorMessage('tb')}}</h6>
                                <!-- OnPart Bot -->
                                <div class="block-value">
                                    <div class="field-name">O<sub>b</sub></div>
                                    <input type="number"
                                           v-model="supportParams.ob.current"
                                           @change="verifySuppRange('ob')"
                                           :min="suppRanges.ob.min"
                                           :max="suppRanges.ob.max"
                                           :step="suppRanges.ob.step">
                                    <div class="unit">mm</div>
                                </div>
                                <h6 class="error" v-if="supportParams.ob.invalid">{{supportErrorMessage('ob')}}</h6>
                                <!-- End Supp Form -->
                                <!-- Support density -->
                                <div class="block-value-range">
                                    <div class="field-name small">Support Density</div>
                                    <div class="tooltip-wrp">
                                        <div class="tooltip">
                                    <span class="tooltipvalue"
                                          :style="{left: parseInt(supportParams.fraction_avg.current * 100) + '%'}">
                                        {{parseInt(supportParams.fraction_avg.current * 100)}}
                                    </span>
                                        </div>
                                        <input type="range"
                                               v-model="supportParams.fraction_avg.current"
                                               @change="verifySuppRange('fraction_avg')"
                                               :min="suppRanges.fraction_avg.min"
                                               :max="suppRanges.fraction_avg.max"
                                               :step="suppRanges.fraction_avg.step">
                                    </div>
                                    <div class="unit">%</div>
                                </div>
                                <div class="block-alert">
                                    Increasing the support density will reduce the size of each support, making the aggregate of
                                    the supports more solid. Example: 100% density means that supports will create a solid "wall"
                                    of supports. Caution! Without teeth, removal of 100% solid supports would require machining.
                                </div>
                                <h6 class="error" v-if="supportParams.fraction_avg.invalid">{{supportErrorMessage('fraction_avg')}}</h6>
                                <!-- End Support density -->
                                <!-- End Supp Form -->
                            </div>
                        </div>
                        <div class="center-align block-wrp block-wrp-33">
                            <draw-support :params="supportParams"/>
                        </div>
                        <div class="center-align block-wrp block-wrp-33">
                            <draw-support :params="supportParams" :buildPlate="true"/>
                        </div>
                    </template>
                </div>
                <div class="row jobSpec">
                    <div class="col s12 center-align">
                        <input type="checkbox" class="filled-in" id="needRemesh"
                               v-model="files[0].needRemesh" @change="toggleRemesh($event)"/>
                        <label for="needRemesh">Match the resolution of the simulation to the resolution of the STL file (Remesh)</label>
                    </div>
                    <!-- <div class="col s12 center-align">
                        <input type="checkbox" class="filled-in" id="isCompensate"
                               v-model="files[0].isCompensate" @change="checkRemesh($event)"/>
                        <label for="isCompensate">Create an additional job result that includes design modifications that compensate for thermal distortion (remeshing is required)</label>
                    </div> -->
                    <div class="col s12 center-align">
                        <input type="checkbox" class="filled-in" id="saveForNext"
                               v-model="saveSpec"/>
                        <label for="saveForNext">Use this job's specifications for the next job</label>
                    </div>
                    <div class="col s12 center-align" v-if="Object.keys(this.fileSupports).length > 0 && !isWithoutSupport">
                        <input type="checkbox" class="filled-in" id="isPreviewSupports"
                               v-model="isPreviewSupports"/>
                        <label for="isPreviewSupports">Preview support in viewer</label>
                    </div>
                </div>
                <div class="row">
                    <div class="col s12 center-align">
                        <h5 class="bold">Prepare file for print run:</h5>
                    </div>
                    <div v-if="loading && statusMessage" class="col s12 center-align status-message">
                        {{statusMessage}}
                    </div>
                    <div v-if="loading && !nestedMode" class="progress">
                        <div class="determinate"
                             v-if="remeshingPart && remeshingProgress > 0"
                             :style="{width: remeshingProgress * 100 + '%'}">
                        </div>
                        <div class="indeterminate" v-else>
                        </div>
                    </div>
                    <div v-if="loading && nestedMode" class="progress">
                        <div class="determinate"
                             v-if="remeshingPart.some(part => part === true) && remeshingProgress > 0"
                             :style="{width: remeshingProgress * 100 + '%'}">
                        </div>
                        <div class="indeterminate" v-else>
                        </div>
                    </div>
                    <div class="col s10 offset-s1" style="margin-bottom: 24px">
                        <h6 class="center-align">
                            You can choose to orient your part manually. AM Build Optimizer™ will automatically add support structures where you need them.
                        </h6>
                    </div>
                    <template v-if="subscription || user.credits.length > 0">
                      <template v-if="!(Object.keys(this.fileSupports).length > 0) || isWithoutSupport">
                          <div class="col s4" v-if="!files[0].isCompensate">
                              <div>
                                  <a :disabled="submitting" @click="submit(false, null)" class="btn a3d multiline">Use AM Build Optimizer Intelligence</a>
                              </div>
                          </div>
                          <div class="col s4" v-if="!files[0].isCompensate">
                              <div>
                                  <a :disabled="submitting" @click="submit(true, false)" class='btn a3d inv multiline'>
                                      Orient Manually &amp; Generate Supports <b>Without</b> Simulation
                                  </a>
                              </div>
                          </div>
                          <div class="col" :class="{'s4': !files[0].isCompensate, 's12': files[0].isCompensate}">
                              <div>
                                  <a :disabled="submitting" @click="submit(true, true)" class='btn a3d inv multiline'>
                                      Orient Manually <b>With</b> Simulation
                                  </a>
                              </div>
                          </div>
                      </template>
                      <template v-else-if="Object.keys(this.fileSupports).length > 0 && !isWithoutSupport">
                          <div class="col offset-s3 s6">
                              <div class="center-align">
                                  <button :disabled="submitting" @click="submit(true, true)" class='btn a3d inv'>
                                      Open preview
                                  </button>
                              </div>
                          </div>
                      </template>
                      <div class="row">
                        <div class="col s4 offset-s4">
                            <div class="center-align" style="margin-bottom:24px;">
                                <h6 v-if="!subscription">This job costs 1 credit.</h6>
                                <h6 class="bold" v-if="files[0].isCompensate">Distortion compensation is not possible without prediction of thermal distortion.</h6>
                            </div>
                        </div>
                      </div>
                    </template>
                    <div class="row" v-else>
                        <div class="col s4 offset-s4">
                            <div class="center-align" style="margin-bottom:24px;">
                                <button @click="buymodal" class='btn a3d'>
                                    Buy Subscription
                                </button>
                                <h6 class="bold">This job costs 1 credit.</h6>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <div style="display: flex; flex-direction: column; align-items: center; padding-top: 20px">
                  <div>
                    <div>
                      <input type="checkbox" class="filled-in" id="isManuallyOrient"
                        @change="changeNestedJobsFlow()"
                        v-model="nestedJobFlow.isManually"/>
                      <label for="isManuallyOrient">Orient my parts manually</label>
                    </div>
                    <div>
                      <input type="checkbox" class="filled-in" id="isManualPlacement"
                            @change="changeNestedJobsFlow()"
                            v-model="nestedJobFlow.isManualPlacement"
                            :disabled="!nestedJobFlow.isManually"
                            />
                      <label for="isManualPlacement">Place my parts on the build tray manually</label>
                    </div>
                  </div>
                </div>
                <div class="blocks-wrp">
                    <!--Printer config-->
                    <h5 class="divider-block center-align block-title bold">
                        Build parameters
                    </h5>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Input thickness of each powder layer (must be consistent with the layer thickness set in your printer):</div>
                            <div class="block-value">
                                <input type="text" id="thicknessfield" v-model="thickness.current"
                                       @change="verifyNumPositive(thickness)"
                                       class="validate">
                                <div class="unit">µm</div>
                            </div>
                            <h6 class="error" v-if="thickness.invalid">Please input a valid thickness.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Input your laser scanning speed:</div>
                            <div class="block-value">
                                <input type="text" id="speedfield" v-model="speed.current" @change="verifyNumPositive(speed)"
                                       class="validate">
                                <div class="unit">mm / s</div>
                            </div>
                            <h6 class="error" v-if="speed.invalid">Please input a valid speed.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Input your laser beam diameter:</div>
                            <div class="block-value">
                                <input type="text" id="diameterfield" v-model="diameter.current" @change="verifyNumPositive(diameter)"
                                       class="validate">
                                <div class="unit">µm</div>
                            </div>
                            <h6 class="error" v-if="diameter.invalid">Please input a valid diameter.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Input your laser power:</div>
                            <div class="block-value">
                                <input type="text" id="powerfield" v-model="power.current" @change="verifyNumPositive(power)"
                                       class="validate">
                                <div class="unit">W</div>
                            </div>
                            <h6 class="error" v-if="power.invalid">Please input a valid power.</h6>
                        </div>
                    </div>
                     <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Input your laser scanning speed for support structures:</div>
                            <div class="block-value">
                                <input type="text" id="vlaserSupp" v-model="vlaserSupp.current" @change="verifyNumPositive(vlaserSupp)"
                                       class="validate">
                                <div class="unit">mm / s</div>
                            </div>
                            <h6 class="error" v-if="vlaserSupp.invalid">Please input a valid laser scanning speed.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                              Number of lasers employed for this build
                            </div>
                            <div class="block-note left-align">
                              (only impacts build time calculation)
                            </div>
                            <div class="block-value">
                                <input type="text" id="nlaserfield" v-model="nlaser.current" @change="verifyNumPositive(nlaser)"
                                       class="validate">
                            </div>
                            <h6 class="error" v-if="nlaser.invalid">Please input a valid number of lasers.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Hatch distance:</div>
                            <div class="block-value">
                                <input type="text" id="hatch" v-model="hatch.current" @change="verifyNumPositive(hatch)"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="hatch.invalid">Please input a valid hatch distance.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">Input your suggested element size:</div>
                            <div class="block-value">
                                <input type="text" id="elementfield" v-model="element.current" @change="verifyNumPositive(element)"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="element.invalid">Please input a valid size.</h6>
                            <div class="block-note">
                                <h6 class="center-align">
                                    (We suggest 2 mm. A smaller element size will result in a more accurate optimization, but a longer processing time.)
                                </h6>
                            </div>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the temperature of the build platform.
                            </div>
                            <div class="block-value">
                                <input type="text" id="tplatefield" v-model="tplate.current"
                                       @change="verifyNum(tplate)"
                                       class="validate">
                                <div class="unit">&deg;C</div>
                            </div>
                            <h6 class="error" v-if="tplate.invalid">Please input a valid temperature.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp" v-if="!nestedJobFlow.isManualPlacement">
                        <div class="block-input">
                            <div class="bold block-description">
                                Input the number of degrees each part should be rotated about the
                                z-axis during each optimization iteration for maximizing the number
                                of parts on the build plate:
                            </div>
                            <div class="block-value">
                                <input type="text" id="deltarfield" v-model="deltaR.current"
                                       @change="verifyNumExactRange(deltaR, 1, 360)"
                                       class="validate">
                                <div class="unit">&deg;</div>
                            </div>
                            <h6 class="error" v-if="deltaR.invalid">Please input a valid degree.</h6>
                            <div class="block-note">
                                <h6 class="center-align">
                                    (30 degrees is recommended. A lower number will potentially result
                                    in a higher density, but the optimization will take longer to
                                    complete. Enter ‘360’ to fix the z rotation.)
                                </h6>
                            </div>
                        </div>
                    </div>
                    <div class="center-align block-wrp" v-if="nestedMode">
                        <div class="block-input">
                            <div class="bold block-description">
                                Build plate margin from the border by X-axis
                            </div>
                            <div class="block-value">
                                <input type="text" id="marginXfield" v-model="marginX.current"
                                       @change="verifyNumExactRange(marginX, 0, parentJobConfig.plateWidth / 2)"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="marginX.invalid">Invalid margin. Must be {{parentJobConfig.plateWidth / 2}} - mm</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp" v-if="nestedMode">
                        <div class="block-input">
                            <div class="bold block-description">
                                Build plate margin from the border by Y-axis
                            </div>
                            <div class="block-value">
                                <input type="text" id="marginYfield" v-model="marginY.current"
                                       @change="verifyNumExactRange(marginY, 0, parentJobConfig.plateHeight / 2)"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="marginY.invalid">Invalid margin. Must be {{parentJobConfig.plateHeight / 2}} - 0 mm</h6>
                        </div>
                    </div>
                </div>
                <div class="blocks-wrp">
                    <h5 class="divider-block center-align block-title bold">
                        Part parameters
                    </h5>
                    <div class="block-full-width">
                        <div class="center-align">
                            <input type="checkbox" class="filled-in" id="useForAll"
                                   @change="switchPartParamAll()"
                                   v-model="partParamsAll"/>
                            <label for="useForAll">Use the same parameters for all nested parts</label>
                        </div>
                    </div>
                    <div class="block-dots top center-align" v-if="!partParamsAll">
                        <div class="tooltip-wrp" v-for="(fileObj, index) of files" :key="fileObj.id">
                            <div class="file-dot"
                                 :class="{'selected': index === fileIndex}"
                                 @click="switchFile(index)"></div>
                            <div class="tooltip">
                                <span class="tooltipvalue">{{fileObj.name}}</span>
                            </div>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the units you selected when creating your .stl file (usually mm or cm):
                            </div>
                            <div class="block-value">
                                <dropdown :visible="unitvisible" :position="position" @clickout="unitvisible = false">
                                    <span class="link btn a3d" @click="unitvisible = true">{{ units[files[fileIndex].unit - 1].name }}
                                        <i class="fa fa-angle-down" aria-hidden="true"></i>
                                    </span>
                                    <div slot="dropdown" class="dialog no-padding">
                                        <aside class="menu">
                                            <ul class="menu-list">
                                                <li v-for="(unit, index) in units" :key="'nested' + unit.name">
                                                    <a class="a3d btn inv" @click="unitChoice(index)">{{unit.name}}</a>
                                                </li>
                                            </ul>
                                        </aside>
                                    </div>
                                </dropdown>
                            </div>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the scaling factor you want to apply when printing the file, relative to your .stl file (example: if you want to print at 150% the dimensions of your .stl file, you would enter 1.50):
                            </div>
                            <div class="block-value">
                                <input type="text" id="scalefield" v-model="files[fileIndex].scale.current"
                                       @change="verifyNumPositive(files[fileIndex].scale)"
                                       class="validate">
                            </div>
                            <h6 class="error" v-if="files[fileIndex].scale.invalid">Please input a valid scale.</h6>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the distance between the part and the build platform.
                            </div>
                            <div class="block-value">
                                <input type="text" id="hsubfield" v-model="files[fileIndex].hsub.current"
                                       @change="verifyNum(files[fileIndex].hsub)"
                                       class="validate">
                                <div class="unit">mm</div>
                            </div>
                            <h6 class="error" v-if="files[fileIndex].hsub.invalid">Please input a valid distance.</h6>
                            <div class="block-note">
                                <h6 class="center-align">
                                    (If the distance is 0, the part will be built directly from the build platform.)
                                </h6>
                            </div>
                        </div>
                    </div>
                    <div class="center-align block-wrp">
                        <div class="block-input">
                            <div class="bold block-description">
                                Please enter the critical angle for generating support structures (in degrees):
                            </div>
                            <div class="block-value">
                                <input type="text" id="critical_anglefield" v-model="files[fileIndex].critical_angle.current"
                                       @change="verifyNum(files[fileIndex].critical_angle)"
                                       class="validate">
                            </div>
                            <h6 class="error" v-if="files[fileIndex].critical_angle.invalid">Please input a valid angle.</h6>
                            <div class="block-note">
                                <h6 class="center-align">
                                    (Surfaces with angles to the horizontal smaller than the critical angle will be supported.)
                                </h6>
                            </div>
                        </div>
                    </div>
                    <div class="center-align block-dots bottom">
                        <input type="checkbox" class="filled-in" id="needRemesh"
                               v-model="files[fileIndex].needRemesh" @change="toggleNestedRemesh($event, fileIndex)"/>
                        <label for="needRemesh">Match the resolution of the simulation to the resolution of the STL file (Remesh)</label>
                    </div>
                    <!--<div class="block-checkbox center-align">
                        <input type="checkbox" class="filled-in" id="isCompensate"
                               v-model="files[fileIndex].isCompensate"/>
                        <label for="isCompensate">Create an additional job result that includes design modifications that compensate for thermal distortion</label>
                    </div>-->
                    <div class="block-dots bottom center-align" v-if="!partParamsAll">
                        <div class="tooltip-wrp" v-for="(fileObj, index) of files" :key="'nested' + fileObj.pathKey">
                            <div class="file-dot"
                                 :class="{'selected': index === fileIndex}"
                                 @click="switchFile(index)"></div>
                            <div class="tooltip">
                                <span class="tooltipvalue">{{fileObj.name}}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row" v-if="nestedMode">
                    <div class="col s6 offset-s3 job-name">
                        <span class="bold">Name of Nested job:</span>
                        <div class="input-field inline">
                            <input id="nestedJobName" type="text" v-model="nestedJobName.current"
                                   @change="verifyString(nestedJobName, 2)">
                        </div>
                        <div class="error center-align" v-if="nestedJobName.invalid">
                            Enter valid job name
                        </div>
                    </div>
                    <div class="col s6 offset-s3 center-align">Enter the name you would like to use to refer to this job in your dashboard</div>
                </div>
                <div class="row">
                    <div v-if="submitting" class="progress">
                        <div class="indeterminate">
                        </div>
                    </div>
                    <div class="col s12 center-align">
                        <button class="a3d btn inv" @click="goBack()" :disabled="submitting">Back</button>
                        <button class="a3d btn" @click="goNext()" :disabled="submitting">Next</button>
                    </div>
                </div>
            </template>
            <div style="flex-grow:1;padding-top:3px;">
                <modal ref='otherprintermodal' name='otherprintermodal' :pivotY='0.2' :reset='true' height='auto'
                       width='70%' @before-close="beforeClosePrinter()">
                    <div style='width:100%;padding:20px' class='row'>
                        <div class="blocks-wrp">
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Printer name:</div>
                                    <div class="block-value">
                                        <input type="text"
                                               id="printerName"
                                               v-model="printerCustom.name.current"
                                               @change="verifyPrinterName(printerCustom.name) && verifyString(printerCustom.name, 2)"
                                               class="validate">
                                    </div>
                                    <h6 class="error" v-if="printerCustom.name.duplicate">Please input unique name.</h6>
                                    <h6 class="error" v-if="printerCustom.name.invalid && !printerCustom.name.duplicate">Please input a valid name.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Build chamber height:</div>
                                    <div class="block-value">
                                        <input type="text" id="chamberHeight" v-model="printerCustom.plateZ.current" @change="verifyNumPositive(printerCustom.plateZ)"
                                               class="validate">
                                        <div class="unit">mm</div>
                                    </div>
                                    <h6 class="error" v-if="printerCustom.plateZ.invalid">Please input a valid height.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Build plate width:</div>
                                    <div class="block-value">
                                        <input type="text" id="plateWidth" v-model="printerCustom.plateX.current" @change="verifyNumPositive(printerCustom.plateX)"
                                               class="validate">
                                        <div class="unit">mm</div>
                                    </div>
                                    <h6 class="error" v-if="printerCustom.plateY.invalid">Please input a valid width.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Build plate length:</div>
                                    <div class="block-value">
                                        <input type="text" id="plateLength" v-model="printerCustom.plateY.current" @change="verifyNumPositive(printerCustom.plateY)"
                                               class="validate">
                                        <div class="unit">mm</div>
                                    </div>
                                    <h6 class="error" v-if="printerCustom.plateX.invalid">Please input a valid length.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Build plate thickness:</div>
                                    <div class="block-value">
                                        <input type="text" id="plateThickness" v-model="printerCustom.plateThickness.current" @change="verifyNumPositive(printerCustom.plateThickness)"
                                               class="validate">
                                        <div class="unit">mm</div>
                                    </div>
                                    <h6 class="error" v-if="printerCustom.plateThickness.invalid">Please input a valid length.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Recoater blade direction:</div>
                                    <div class="block-value column">
                                        <input name="bladeDirection"
                                               type="radio"
                                               id="width"
                                               v-model="printerCustom.DirectionRecoater.current"
                                               value="width" />
                                        <label for="width">Blade travels across the plate width</label>
                                        <input name="bladeDirection"
                                               type="radio"
                                               id="length"
                                               v-model="printerCustom.DirectionRecoater.current"
                                               value="length" />
                                        <label for="length">Blade travels across the plate length</label>
                                    </div>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Default Infill laser beam scan speed:</div>
                                    <div class="block-value">
                                        <input type="text" id="scanningSpeed" v-model="printerCustom.speed.current" @change="verifyNumPositive(printerCustom.speed)"
                                               class="validate">
                                        <div class="unit">mm / s</div>
                                    </div>
                                    <h6 class="error" v-if="printerCustom.speed.invalid">Please input a valid scan speed.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Build Time Calibration Factor:</div>
                                    <div class="block-value">
                                        <input type="text" id="calibrationFactor" v-model="printerCustom.calibrationFactor.current" @change="verifyNumBetween(printerCustom.calibrationFactor, 0, 2)"
                                               class="validate">
                                    </div>
                                    <h6 class="error" v-if="printerCustom.calibrationFactor.invalid">Build Time Calibration Factor should be between 0 and 2.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Default Infill laser beam diameter:</div>
                                    <div class="block-value">
                                        <input type="text" id="beamDiameter" v-model="printerCustom.diameter.current" @change="verifyNumPositive(printerCustom.diameter)"
                                               class="validate">
                                        <div class="unit">µm</div>
                                    </div>
                                    <h6 class="error" v-if="printerCustom.diameter.invalid">Please input a valid diameter.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Default Infill laser beam power:</div>
                                    <div class="block-value">
                                        <input type="text" id="laserPower" v-model="printerCustom.power.current" @change="verifyNumPositive(printerCustom.power)"
                                               class="validate">
                                        <div class="unit">W</div>
                                    </div>
                                    <h6 class="error" v-if="printerCustom.power.invalid">Please input a valid power.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Default thickness of each powder layer (must be consistent with the layer thickness set in your printer):</div>
                                    <div class="block-value">
                                        <input type="text" id="layerThickness" v-model="printerCustom.thickness.current"
                                               @change="verifyNumPositive(printerCustom.thickness)"
                                               class="validate">
                                        <div class="unit">µm</div>
                                    </div>
                                    <h6 class="error" v-if="printerCustom.thickness.invalid">Please input a valid thickness.</h6>
                                </div>
                            </div>
                        </div>
                        <div class="center-align block-button">
                            <button class="a3d btn" @click="addPrinter()">Save printer</button>
                        </div>
                        <div class='col s10 offset-s1 center-align'>
                            AM Build Optimizer should work on any DMLS printer - however, only the printers listed have been validated.
                            Please enter your printer’s make and model so we can add it to our validation work and eventually add it to the dropdown list.
                            Please click the button below to send us an email with information to help us optimize for your printer.
                        </div>
                        <div class='col s10 offset-s1 center-align'>
                            <a class="a3d btn small inv" style='margin-top:20px; width:50%'
                               href="mailto:support@atlas3d.xyz?subject=Job%20with%20Unsupported%20Material?body=Please%20insert%20the%20following%20information%2C%20and%20any%20other%20relevant%20information:%20Printer%20Make%2C%20Printer%20model.">Send Email
                            </a>
                        </div>
                    </div>
                </modal>
                <modal ref='othermaterialmodal' name='othermaterialmodal' :pivotY='0.2' :reset='true' height='auto'
                       width='70%' @before-close="beforeCloseMaterial()">
                    <div style='width:100%;padding:20px' class='row'>
                        <div class="blocks-wrp">
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Material name:</div>
                                    <div class="block-value">
                                        <input type="text"
                                               id="materialName"
                                               v-model="materialCustom.name.current"
                                               @change="verifyMaterialName(materialCustom.name) && verifyString(materialCustom.name, 2)"
                                               class="validate">
                                    </div>
                                    <h6 class="error" v-if="materialCustom.name.duplicate">Please input unique name.</h6>
                                    <h6 class="error" v-if="materialCustom.name.invalid && !materialCustom.name.duplicate">Please input a valid name.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Poisson's Ratio:</div>
                                    <div class="block-value">
                                        <input type="text" id="poissonsRatio" v-model="materialCustom.nu.current" @change="verifyNumPositive(materialCustom.nu)"
                                               class="validate">
                                    </div>
                                    <h6 class="error" v-if="materialCustom.nu.invalid">Please input a valid Poisson's Ratio.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Young's modulus:</div>
                                    <div class="block-value">
                                        <input type="text" id="elasticity" v-model="materialCustom.E.current" @change="verifyNumPositive(materialCustom.E)"
                                               class="validate">
                                        <div class="unit">GPa</div>
                                    </div>
                                    <h6 class="error" v-if="materialCustom.E.invalid">Please input a valid Young's modulus.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Young's modulus at high temperatures:</div>
                                    <template v-for="(value, valueIndex) in materialCustom.E_heat">
                                        <div class="block-value" :key="valueIndex">
                                            <div class="icon-remove" v-if="materialCustom.E_heat.length > 1">
                                                <i class="material-icons a3d color" style="cursor:pointer;" @click="removeMaterialHeatDependentValue(materialCustom.E_heat, valueIndex)">
                                                    clear
                                                </i>
                                            </div>
                                            <input type="text" v-model="materialCustom.E_heat[valueIndex].temperature.current" @change="verifyNumPositive(materialCustom.E_heat[valueIndex].temperature)"
                                                   class="validate">
                                            <div class="unit first">&deg;C</div>
                                            <input type="text" v-model="materialCustom.E_heat[valueIndex].value.current" @change="verifyNumPositive(materialCustom.E_heat[valueIndex].value)" class="validate">
                                            <div class="unit">GPa</div>
                                        </div>
                                        <h6 class="error" v-if="materialCustom.E_heat[valueIndex].temperature.invalid" :key="valueIndex">Please input a valid temperature.</h6>
                                        <h6 class="error" v-if="materialCustom.E_heat[valueIndex].value.invalid" :key="valueIndex">Please input a valid Young's modulus.</h6>
                                    </template>
                                    <div class="block-buttons">
                                        <button class="a3d btn" @click="addMaterialHeatDependentValue(materialCustom.E_heat)">Add</button>
                                    </div>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Density:</div>
                                    <div class="block-value">
                                        <input type="text" id="density" v-model="materialCustom.rho.current" @change="verifyNumPositive(materialCustom.rho)"
                                               class="validate">
                                        <div class="unit">kg/m<sup>3</sup></div>
                                    </div>
                                    <h6 class="error" v-if="materialCustom.rho.invalid">Please input a valid Density.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Specific Heat:</div>
                                    <div class="block-value">
                                        <input type="text" id="specificHeat" v-model="materialCustom.Cp.current" @change="verifyNumPositive(materialCustom.Cp)"
                                               class="validate">
                                        <div class="unit">J/(kgK)</div>
                                    </div>
                                    <h6 class="error" v-if="materialCustom.Cp.invalid">Please input a valid Specific Heat.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Thermal Conductivity:</div>
                                    <div class="block-value">
                                        <input type="text" id="thermalConductivity" v-model="materialCustom.k.current" @change="verifyNumPositive(materialCustom.k)"
                                               class="validate">
                                        <div class="unit">W/(mK)</div>
                                    </div>
                                    <h6 class="error" v-if="materialCustom.k.invalid">Please input a valid Thermal Conductivity.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Solidus Temperature:</div>
                                    <div class="block-value">
                                        <input type="text" id="solidusTemp" v-model="materialCustom.Tsolid.current" @change="verifyNumPositive(materialCustom.Tsolid)"
                                               class="validate">
                                        <div class="unit">&deg;C</div>
                                    </div>
                                    <h6 class="error" v-if="materialCustom.Tsolid.invalid">Please input a valid temperature.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Liquidus temperature:</div>
                                    <div class="block-value">
                                        <input type="text" id="liquidusTemp" v-model="materialCustom.Tliquid.current" @change="verifyNumPositive(materialCustom.Tliquid)"
                                               class="validate">
                                        <div class="unit">&deg;C</div>
                                    </div>
                                    <h6 class="error" v-if="materialCustom.Tliquid.invalid">Please input a valid temperature.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Yield Stress:</div>
                                    <div class="block-value">
                                        <input type="text" id="yieldStress" v-model="materialCustom.sigma_y.current" @change="verifyNumPositive(materialCustom.sigma_y)"
                                               class="validate">
                                        <div class="unit">MPa</div>
                                    </div>
                                    <h6 class="error" v-if="materialCustom.sigma_y.invalid">Please input a valid Yield Stress.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Yield Stress at high temperatures:</div>
                                    <template v-for="(value, valueIndex) in materialCustom.sigma_y_heat">
                                        <div class="block-value" :key="valueIndex">
                                            <div class="icon-remove" v-if="materialCustom.sigma_y_heat.length > 1">
                                                <i class="material-icons a3d color" style="cursor:pointer;" @click="removeMaterialHeatDependentValue(materialCustom.sigma_y_heat, valueIndex)">
                                                    clear
                                                </i>
                                            </div>
                                            <input type="text" v-model="materialCustom.sigma_y_heat[valueIndex].temperature.current" @change="verifyNumPositive(materialCustom.sigma_y_heat[valueIndex].temperature)"
                                                   class="validate">
                                            <div class="unit first">&deg;C</div>
                                            <input type="text" v-model="materialCustom.sigma_y_heat[valueIndex].value.current" @change="verifyNumPositive(materialCustom.sigma_y_heat[valueIndex].value)" class="validate">
                                            <div class="unit">MPa</div>
                                        </div>
                                        <h6 class="error" v-if="materialCustom.sigma_y_heat[valueIndex].temperature.invalid" :key="valueIndex">Please input a valid temperature.</h6>
                                        <h6 class="error" v-if="materialCustom.sigma_y_heat[valueIndex].value.invalid" :key="valueIndex">Please input a valid Young's modulus.</h6>
                                    </template>
                                    <div class="block-buttons">
                                        <button class="a3d btn" @click="addMaterialHeatDependentValue(materialCustom.sigma_y_heat)">Add</button>
                                    </div>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Melting Latent Heat:</div>
                                    <div class="block-value">
                                        <input type="text" id="meltingHeat" v-model="materialCustom.hsl.current" @change="verifyNumPositive(materialCustom.hsl)"
                                               class="validate">
                                        <div class="unit">kJ/kg</div>
                                    </div>
                                    <h6 class="error" v-if="materialCustom.hsl.invalid">Please input a valid Melting Latent Heat.</h6>
                                </div>
                            </div>
                            <div class="center-align block-wrp">
                                <div class="block-input">
                                    <div class="bold block-description">Thermal Expansion Coefficient:</div>
                                    <div class="block-value">
                                        <input type="text" id="thermalExpansion" v-model="materialCustom.alpha.current" @change="verifyNumPositive(materialCustom.alpha)"
                                               class="validate">
                                        <div class="unit">10<sup>-6</sup>/&deg;C</div>
                                    </div>
                                    <h6 class="error" v-if="materialCustom.alpha.invalid">Please input a valid Thermal Expansion Coefficient.</h6>
                                </div>
                            </div>
                        </div>
                        <div class="center-align block-button">
                            <button class="a3d btn" @click="addMaterial(false)">Save material</button>
                        </div>
                        <div class='col s10 offset-s1 center-align'>
                            AM Build Optimizer has not been validated with your material.
                            We will attempt to gather the required material properties and eventually add it to the dropdown list.
                            Please click the button below to send us an email with information to help optimize for your material.
                        </div>
                        <div class='col s10 offset-s1 center-align'>
                            <a class="a3d btn small inv" style='margin-top:20px;width:50%;'
                               href="mailto:support@atlas3d.xyz?subject=Job%20with%20Unsupported%20Material?body=Please%20insert%20the%20following%20information%2C%20and%20any%20other%20relevant%20information:%20Material%20name%2C%20material%20description%2C%20and%20source.">Send Email
                            </a>
                        </div>
                    </div>
                </modal>
                <modal ref="cautionmodal" name="cautionmodal" :pivotY="0.4" :reset="true" height="auto" width="50%">
                    <div class="caution-modal">
                        <div class="text-block">
                            <b>Caution:</b> Changing these parameters may prevent your
                            part from printing successfully. If you are an advanced user, please
                            proceed and make your desired changes.
                        </div>
                        <div class="text-block">
                            Click "OK" to proceed, or "Cancel" to return to the previous screen.
                        </div>
                        <div class="row">
                            <div class="col s6 right-align">
                                <button class="a3d btn" @click="acceptModal(true)">OK</button>
                            </div>
                            <div class="col s6 left-align">
                                <button class="a3d btn inv" @click="acceptModal(false)">Cancel</button>
                            </div>
                        </div>
                    </div>
                </modal>
            </div>
        </div>
    </div>
</template>

<script>
  import Banner from './Banner.vue'
  import DrawSupport from './CreateForm/SupportCanvas.vue'
  import { mapState, mapGetters, mapActions } from 'vuex'
  import dropdown from 'vue-my-dropdown'
  import store from '@/store'
  import errorHandling from '../mixins/errorHandling'
  import { defaultConfig } from '../services/helpers'

  export default {
    name: 'home',
    mixins: [errorHandling],
    data: function () {
      return {
        nestedMode: false,
        parentPathKey: null,
        parentJobConfig: {
          plateWidth: 250,
          plateHeight: 250,
          plateThickness: 30,
          precision: 0.5,
          spacing: 2,
          delta: 2,
          deltaR: 30,
          plateMode: 0,
          plateDiameter: 250
        },
        partParamsAll: true,
        files: [],
        fileIndex: 0,
        file: null,
        fileLoading: true,
        fileSupports: {},
        isPreviewSupports: true,
        failure: false,
        confirming: false,
        loading: false,
        submitting: false,
        remeshingPart: [false],
        remeshingProgress: null,
        statusMessage: null,
        checkElementSizeProcess: false,
        checkElementSizeResult: null,
        checkElementSizeError: false,
        nestedJobFlow: {isManually: false, isManualPlacement: false},
        unitvisible: false,
        unitsuppvisible: false,
        materialvisible: false,
        printervisible: false,
        structurevisible: false,
        saveSpec: false,
        showAdvanced: false,
        printerAdded: false,
        materialAdded: false,
        x: 0,
        y: 0,
        z: 0,
        nestedJobName: {last: '', current: '', invalid: false},
        deltaR: {last: '', current: '30', invalid: false},
        marginY: {last: 0, current: '0', invalid: false},
        marginX: {last: 0, current: '0', invalid: false},
        defaultFileConfig: {
          scale: {last: '', current: defaultConfig.scaling[0], invalid: false},
          hsub: {last: '', current: defaultConfig.hsub[0], invalid: false},
          suppSpacing_xy: {last: '', current: defaultConfig.suppSpacing_xy[0], invalid: false},
          suppHatchSize: {last: '', current: defaultConfig.suppHatchSize[0], invalid: false},
          supp_size_boundary: {last: '', current: defaultConfig.supp_size_boundary[0], invalid: false},
          critical_angle: {last: '', current: defaultConfig.critical_angle[0], invalid: false},
          isCompensate: false,
          unit: 1,
          unit_supp: 0,
          filename_supp: '',
          needRemesh: false
        },
        power: {last: '', current: defaultConfig.Power_laser[0], invalid: false},
        diameter: {last: '', current: defaultConfig.Diameter_laser[0], invalid: false},
        thickness: {last: '', current: defaultConfig.layerThickness[0], invalid: false},
        speed: {last: '', current: defaultConfig.Speed_laser[0], invalid: false},
        element: {last: '', current: defaultConfig.elementsize[0] * 1000, invalid: false}, // value is in mm, in config should be m
        tplate: {last: '', current: defaultConfig.Tplate[0], invalid: false},
        nlaser: {last: '', current: defaultConfig.NLaser[0], invalid: false},
        vlaserSupp: {last: '', current: defaultConfig.VlaserSupp[0], invalid: false},
        hatch: {last: '', current: defaultConfig.hatch[0], invalid: false},
        scalingX_material: {last: '', current: defaultConfig.scalingX_material[0], invalid: false},
        scalingY_material: {last: '', current: defaultConfig.scalingY_material[0], invalid: false},
        material: defaultConfig.material[0],
        printer: 1,
        structure: 1,
        H_buildPlate: {last: '', current: defaultConfig.H_buildPlate[0], invalid: false},
        CrashTolerance: {last: '', current: defaultConfig.CrashTolerance[0], invalid: false},
        fragmentationSize: {last: '', current: defaultConfig.fragmentationSize[0], invalid: false},
        tetraEps: {last: '', current: defaultConfig.tetraEps[0], invalid: false},
        compensationScale: {last: '', current: defaultConfig.compensationScale[0], invalid: false},
        tetraMeshSize: {last: '', current: defaultConfig.tetraMeshSize[0], invalid: false},
        isWithoutSupport: false,
        isRepair: false,
        isStrengthSupp: false,
        isEvalFacet: true,
        isEvalVertex: true,
        isEvalEdge: true,
        isFragmentation: false,
        position: ['center', 'top', 'center', 'top'],
        units: [
          {name: 'mm'},
          {name: 'cm'},
          {name: 'm'},
          {name: 'inch'}
        ],
        units_supp: [
          {name: 'mm'},
          {name: 'cm'},
          {name: 'inch'}
        ],
        structures: [
          {name: 'Block', img: '/static/img/block.jpg'},
          {name: 'Point', img: '/static/img/point.jpg'},
          {name: 'Web', img: '/static/img/web.jpg'},
          {name: 'Contour', img: '/static/img/contour.jpg'},
          {name: 'Line', img: '/static/img/line.jpg'}
        ],
        printersConfig: {},
        printerCustom: {
          name: {last: '', current: '', invalid: false, duplicate: false},
          DirectionRecoater: {last: '', current: 'width', invalid: false},
          diameter: {last: '', current: '100', invalid: false},
          power: {last: '', current: '200', invalid: false},
          speed: {last: '', current: '500', invalid: false},
          thickness: {last: '', current: '40', invalid: false},
          plateX: {last: '', current: '250', invalid: false},
          plateY: {last: '', current: '250', invalid: false},
          plateZ: {last: '', current: '250', invalid: false},
          plateThickness: {last: '', current: '30', invalid: false},
          plateMode: {last: '', current: '0', invalid: false},
          plateDiameter: {last: '', current: '250', invalid: false},
          calibrationFactor: {last: '', current: '1', invalid: false}
        },
        materialsConfig: {},
        materialCustom: {
          name: {last: '', current: '', invalid: false, duplicate: false},
          nu: {last: '', current: '', invalid: false},
          E: {last: '', current: '', invalid: false},
          E_heat: [{
            temperature: {last: '', current: '', invalid: false},
            value: {last: '', current: '', invalid: false}
          }],
          rho: {last: '', current: '', invalid: false},
          Cp: {last: '', current: '', invalid: false},
          k: {last: '', current: '', invalid: false},
          Tsolid: {last: '', current: '', invalid: false},
          Tliquid: {last: '', current: '', invalid: false},
          sigma_y: {last: '', current: '', invalid: false},
          sigma_y_heat: [{
            temperature: {last: '', current: '', invalid: false},
            value: {last: '', current: '', invalid: false}
          }],
          hsl: {last: '', current: '', invalid: false},
          alpha: {last: '', current: '', invalid: false}
        },
        suppRanges: {
          wt: {min: 0, max: 100},
          pt: {min: 0, max: 1, step: 0.01},
          tt: {min: 0, max: 5, step: 0.01},
          ot: {min: 0, max: 5, step: 0.01},
          wb: {min: 0, max: 100},
          pb: {min: 0, max: 1, step: 0.01},
          tb: {min: 0, max: 5, step: 0.01},
          ob: {min: 0, max: 5, step: 0.01},
          fraction_avg: {min: 0, max: 1, step: 0.01}
        },
        supportParams: {
          wt: {last: '50', current: '50', invalid: false},
          pt: {last: '0.16', current: '0.16', invalid: false},
          tt: {last: '0.5', current: '0.5', invalid: false},
          ot: {last: '0.8', current: '0.8', invalid: false},
          wb: {last: '80', current: '80', invalid: false},
          pb: {last: '0.16', current: '0.16', invalid: false},
          tb: {last: '0.5', current: '0.5', invalid: false},
          ob: {last: '0.8', current: '0.8', invalid: false},
          fraction_avg: {last: '0.42', current: '0.42', invalid: false}
        },
        supportParamsDefault: {
          wt: '50',
          pt: '0.16',
          tt: '0.5',
          ot: '0.8',
          wb: '80',
          pb: '0.16',
          tb: '0.5',
          ob: '0.8',
          fraction_avg: '0.42'
        },
        uploadSupportsMessage: null
      } // End return data
    },
    components: {
      dropdown,
      'dash-banner': Banner,
      DrawSupport
    },
    props: ['auth', 'authenticated'],
    methods: {
      ...mapActions({
        refreshCredits: 'user/refreshCredits'
      }),
      ...mapActions([
        // 'getJobs',
        'uploadFile',
        'refreshFiles',
        'createJob',
        'updateJob',
        'submitJob',
        'createNestedJob',
        'updateNestedJob',
        'deleteFile',
        'getJobById',
        'getNestedJobs',
        'getJob',
        'setNestedFileData',
        'checkElementSize',
        'getURLData',
        'remeshPart',
        'getPrintConfig'
      ]),
      buymodal: function () {
        this.$modal.show('details')
      },
      selectSupportsFile: function () {
        document.getElementById('uploadsupports').click()
      },
      uploadSupports: function () {
        const uploadSupportsInput = document.getElementById('uploadsupports')
        const supportFiles = Array.from(uploadSupportsInput.files)
        if (supportFiles.length > 0) {
          if (supportFiles.every((supportFile) => supportFile.name.match(/.(stl)$/i))) {
            this.uploadSupportsMessage = null
            supportFiles.forEach((supportFile) => {
              this.fileSupports = Object.assign({[supportFile.name]: supportFile}, this.fileSupports)
            })
            // if (this.element.last === '') {
            //   this.element.current = 1
            // }
          } else {
            this.uploadSupportsMessage = 'Sorry, your file(s) is an invalid file type. Please upload only STL files at this time.'
          }
        } else {
          this.removeSupportsFile()
        }
        uploadSupportsInput.value = ''
      },
      removeSupportsFile: function (fileSupport) {
        document.getElementById('uploadsupports').value = ''
        this.uploadSupportsMessage = null
        // if (this.element.last === '') {
        //   this.element.current = 3
        // }
        if (fileSupport) {
          this.fileSupports = Object.keys(this.fileSupports).reduce((acc, curr) => {
            if (curr !== fileSupport) {
              acc[curr] = this.fileSupports[curr]
            }
            return acc
          }, {})
        }
      },
      calculateElementSize: function () {
        if (this.verifyNumPositive(this.defaultFileConfig.scale) &&
          this.verifyNumPositive(this.element) &&
          this.verifyNumPositive(this.power) &&
          this.verifyNumPositive(this.diameter) &&
          this.verifyNumPositive(this.speed) &&
          this.verifyNumPositive(this.thickness) &&
          this.verifyNumPositive(this.nlaser) &&
          this.verifyNumPositive(this.vlaserSupp) &&
          this.verifyNumPositive(this.hatch) &&
          this.verifyNumPositive(this.CrashTolerance) &&
          this.verifyNumPositive(this.fragmentationSize) &&
          this.verifyNumPositive(this.tetraEps) &&
          this.verifyNumPositive(this.tetraMeshSize) &&
          this.verifyNumPositive(this.compensationScale) &&
          this.verifyNum(this.tplate)) {
          this.checkElementSizeProcess = true
          this.calculateElementSizeReset()
          this.checkElementSize({
            sourceFilePathKey: this.file.pathKey,
            config: this.config
          })
            .then((response) => {
              this.calculateElementSizeGetData(response.data.url, 1)
            })
        }
      },
      calculateElementSizeGetData: function (url, attempt) {
        this.getURLData({url})
          .then((optimizerResponse) => {
            if (optimizerResponse.data && optimizerResponse.data.message) {
              this.checkElementSizeProcess = false
              this.checkElementSizeResult = optimizerResponse.data.message
            }
          })
          .catch((err) => {
            if (err.response &&
              err.response.status &&
              err.response.status === 404 && attempt <= 10) {
              attempt++
              let delay = 1500 * attempt
              if (attempt > 3) {
                delay = 5000
              }
              setTimeout(() => {
                this.calculateElementSizeGetData(url, attempt)
              }, delay)
            } else {
              this.checkElementSizeProcess = false
              this.checkElementSizeResult = 'Something went wrong. Please try again'
              this.checkElementSizeError = true
            }
          })
      },
      calculateElementSizeReset: function () {
        this.checkElementSizeError = false
        this.checkElementSizeResult = null
      },
      submit: function (isManually, thermalPredict) {
        if (this.verifyNumPositive(this.defaultFileConfig.scale) &&
          this.verifyNumPositive(this.element) &&
          this.verifyNumPositive(this.power) &&
          this.verifyNumPositive(this.nlaser) &&
          this.verifyNumPositive(this.CrashTolerance) &&
          this.verifyNumPositive(this.fragmentationSize) &&
          this.verifyNumPositive(this.tetraEps) &&
          this.verifyNumPositive(this.tetraMeshSize) &&
          this.verifyNumPositive(this.diameter) &&
          this.verifyNumPositive(this.speed) &&
          this.verifyNumPositive(this.thickness) &&
          this.verifyNumPositive(this.compensationScale) &&
          this.verifyNumPositiveFromZero(this.scalingX_material) &&
          this.verifyNumPositiveFromZero(this.scalingY_material) &&
          this.verifyNum(this.tplate)) {
          this.loading = true
          this.submitting = true
          this.saveSpecifications()

          let jobPathKey = null
          const needRemesh = this.files[0].needRemesh && (thermalPredict || thermalPredict === false)
          const remeshPromise = needRemesh ? this.remeshSourceFile(this.files[0], 0, thermalPredict) : Promise.resolve()
          return remeshPromise.then(() => this.processJob())
          .then(response => {
            jobPathKey = response.data.jobPathKey
            if (Object.keys(this.fileSupports).length > 0) {
              this.statusMessage = 'Uploading supports...'
              return Promise.all(Object.keys(this.fileSupports).map((fileSupport) => {
                return this.uploadFile({
                  file: this.fileSupports[fileSupport],
                  sourceFilePathKey: this.file.pathKey,
                  jobPathKey: response.data.jobPathKey
                })
              }))
            } else {
              return Promise.resolve('Skip supports upload')
            }
          })
            .then(() => {
              this.refreshCredits()
              if (isManually) {
                this.$router.push({name: 'ThreeJSViewer', params: {id: jobPathKey, type: 'input', createConfig: {...this.config, needRemesh}}, query: {shouldPredict: thermalPredict}})
              } else {
                this.$router.replace('/dash/magic/' + jobPathKey)
              }
              this.submitting = false
              this.loading = false
              this.statusMessage = null
            })
            .catch((err) => {
              this.errorHandling(err, () => {
                this.submit(isManually, thermalPredict)
              })
              this.submitting = false
              this.loading = false
              this.statusMessage = null
            })
        }
      },

      processJob () {
        if (this.$route.params.jobPathKey) {
          this.statusMessage = 'Updating job...'
          return this.updateJob({
            jobPathKey: this.$route.params.jobPathKey,
            config: this.config,
            isArchived: false
          })
        } else {
          this.statusMessage = 'Creating job...'
          return this.createJob({
            sourceFilePathKey: this.file.pathKey,
            config: this.config
          })
        }
      },

      remeshPartAttempt: function (url, attempt) {
        return new Promise((resolve, reject) => {
          this.getURLData({url})
            .then((response) => {
              if (response.data) {
                if (response.data.result === true) {
                  resolve()
                } else {
                  reject({message: 'Server error'})
                }
              }
            })
            .catch((err) => {
              if (err.response &&
                err.response.status &&
                err.response.status === 404 &&
                attempt <= 42) {
                attempt++
                let delay = 1500 * attempt
                if (attempt > 15) {
                  delay = 30000
                } else if (attempt > 3) {
                  delay = 10000
                }
                setTimeout(() => {
                  resolve(this.remeshPartAttempt(url, attempt))
                }, delay)
              } else {
                reject(err)
              }
            })
        })
      },
      remeshPartProgress: function (url, index) {
        if (this.remeshingPart[index]) {
          this.getURLData({url})
            .then((response) => {
              if (!this.nestedMode) {
                this.remeshingProgress = response.data.progress
              } else {
                this.remeshingProgress = (response.data.progress + this.remeshingProgress) / this.filesNested.length
              }
              setTimeout(() => {
                this.remeshPartProgress(url)
              }, 2500)
            })
            .catch(() => {
              console.log('Getting remesh progress...')
              setTimeout(() => {
                this.remeshPartProgress(url)
              }, 2500)
            })
        }
      },
      remeshSourceFile (file, index) {
        let newConf = {...this.config}
        if (this.nestedMode) {
          newConf.filename = file.name
        }
        this.statusMessage = 'Remeshing in progress...'
        this.remeshingPart[index] = true
        return new Promise((resolve, reject) => {
          let filename
          this.remeshPart({
            sourceFilePathKey: file.pathKey,
            config: newConf
          })
          .then((res) => {
            filename = res.data.filename
            this.remeshPartProgress(res.data.progress, index)
            return this.remeshPartAttempt(res.data.url, 1)
          })
          .then(() => {
            this.remeshingPart[index] = false
            if (filename) {
              file.name = filename
              file.needRemesh = false
            }
            resolve()
          })
          .catch((err) => {
            if (err) {
              console.log('Remeshing error')
            }
            resolve(null)
          })
        })
      },

      supportErrorMessage: function (paramname) {
        const {min, max} = this.suppRanges[paramname]
        return `Must be a number in range from ${min} to ${max}`
      },
      saveSpecifications: function () {
        let jobSpecification = {}
        if (this.saveSpec) {
          jobSpecification.material = this.material
          jobSpecification.printer = this.printer
          jobSpecification.scale = this.files[0].scale.current
          jobSpecification.unit = this.files[0].unit
          jobSpecification.speed = this.speed.current
          jobSpecification.diameter = this.diameter.current
          jobSpecification.power = this.power.current
          jobSpecification.nlaser = this.nlaser.current
          jobSpecification.vlaserSupp = this.vlaserSupp.current
          jobSpecification.hatch = this.hatch.current
          jobSpecification.CrashTolerance = this.CrashTolerance.current
          jobSpecification.fragmentationSize = this.fragmentationSize.current
          jobSpecification.tetraEps = this.element.tetraEps
          jobSpecification.tetraMeshSize = this.element.tetraMeshSize
          jobSpecification.thickness = this.thickness.current
          jobSpecification.element = this.element.current
          jobSpecification.critical_angle = this.files[0].critical_angle.current
          jobSpecification.hsub = this.files[0].hsub.current
          jobSpecification.suppSpacing_xy = this.files[0].suppSpacing_xy.current
          jobSpecification.suppHatchSize = this.files[0].suppHatchSize.current
          jobSpecification.supp_size_boundary = this.files[0].supp_size_boundary.current
          jobSpecification.tplate = this.tplate.current
          jobSpecification.isCompensate = this.files[0].isCompensate
          jobSpecification.wt = this.supportParams.wt.current
          jobSpecification.pt = this.supportParams.pt.current
          jobSpecification.tt = this.supportParams.tt.current
          jobSpecification.ot = this.supportParams.ot.current
          jobSpecification.wb = this.supportParams.wb.current
          jobSpecification.pb = this.supportParams.pb.current
          jobSpecification.tb = this.supportParams.tb.current
          jobSpecification.ob = this.supportParams.ob.current
          jobSpecification.fraction_avg = this.supportParams.fraction_avg.current
          jobSpecification.isRepair = this.isRepair
          jobSpecification.isStrengthSupp = this.isStrengthSupp
          jobSpecification.isEvalFacet = this.isEvalFacet
          jobSpecification.isEvalVertex = this.isEvalVertex
          jobSpecification.isEvalEdge = this.isEvalEdge
          jobSpecification.isFragmentation = this.isFragmentation
          jobSpecification.H_buildPlate = this.H_buildPlate.current
          jobSpecification.scalingX_material = this.scalingX_material.current
          jobSpecification.scalingY_material = this.scalingY_material.current
          localStorage.setItem('jobSpecification', JSON.stringify(jobSpecification))
        } else {
          localStorage.removeItem('jobSpecification')
        }
      },
      verifyNum: function (sNum) {
        if (isNaN(sNum.current) || sNum.current === '') {
          sNum.current = sNum.last
          sNum.invalid = true
          return false
        } else {
          sNum.last = sNum.current
          sNum.invalid = false
          return true
        }
      },
      verifyNumPositive: function (sNum) {
        if (isNaN(sNum.current) || sNum.current === '' || sNum.current <= 0) {
          sNum.current = sNum.last
          sNum.invalid = true
          return false
        } else {
          sNum.last = sNum.current
          sNum.invalid = false
          return true
        }
      },
      verifyNumPositiveFromZero: function (sNum) {
        if (isNaN(sNum.current) || sNum.current === '' || sNum.current < 0) {
          sNum.current = sNum.last
          sNum.invalid = true
          return false
        } else {
          sNum.last = sNum.current
          sNum.invalid = false
          return true
        }
      },
      verifyString: function (sString, length) {
        if (sString.current.length < length) {
          sString.current = sString.last
          sString.invalid = true
          return false
        } else {
          sString.last = sString.current
          sString.invalid = false
          return true
        }
      },
      verifyNumBetween: function (sNum, min, max) {
        if (isNaN(sNum.current) || sNum.current === '' || sNum.current <= min || sNum.current >= max) {
          sNum.current = sNum.last
          sNum.invalid = true
          return false
        } else {
          sNum.last = sNum.current
          sNum.invalid = false
          return true
        }
      },
      verifyNumExactRange: function (sNum, min, max) {
        if (isNaN(sNum.current) || sNum.current === '' || sNum.current < min || sNum.current > max) {
          sNum.current = sNum.last
          sNum.invalid = true
          return false
        } else {
          sNum.last = sNum.current
          sNum.invalid = false
          return true
        }
      },
      verifySuppRange: function (paramname) {
        const range = (min, max) => number => {
          if (number >= min && number <= max) {
            return number
          } else if (number < min) {
            return min
          } else if (number > max) {
            return max
          }
        }
        const {current, last} = this.supportParams[paramname]
        const {min, max} = this.suppRanges[paramname]
        const numberNotNaN = isNaN(current) === false
        const restrict = range(Number(min), Number(max))
        const allowedNum = restrict(Number(current))
        const numberIsInRange = Number(current) === allowedNum

        if (numberNotNaN && numberIsInRange) {
          this.supportParams[paramname].last = current
          this.supportParams[paramname].invalid = false
        } else {
          this.supportParams[paramname].current = last
          this.supportParams[paramname].invalid = true
        }
      },
      verifyPrinterName: function (name) {
        if (this.printersConfig.hasOwnProperty(name.current)) {
          name.current = name.last
          name.invalid = true
          name.duplicate = true
          return false
        } else {
          name.last = name.current
          name.invalid = false
          name.duplicate = false
          return true
        }
      },
      verifyMaterialName: function (name) {
        if (this.materialsConfig.hasOwnProperty(name.current) || this.materials.find((material) => {
          return material.name === name.current
        })) {
          name.current = name.last
          name.invalid = true
          name.duplicate = true
          return false
        } else {
          name.last = name.current
          name.invalid = false
          name.duplicate = false
          return true
        }
      },
      verifyHeatDependentValues: function (param) {
        let flag = true
        param.forEach((item) => {
          if (!(this.verifyNumPositive(item.temperature) && this.verifyNumPositive(item.value))) {
            flag = false
          }
        })
        return flag
      },
      parseHeatDependentValues: function (param, directionForward) {
        if (directionForward) {
          const result = []
          param.forEach((item) => {
            result.push(Number(item.temperature.current))
            result.push(Number(item.value.current))
          })
          return result
        } else {
          const resultBack = []
          param.forEach((item, index) => {
            if (!resultBack[Math.floor(index / 2)]) {
              resultBack[Math.floor(index / 2)] = {}
            }
            if (index % 2 === 0) {
              resultBack[Math.floor(index / 2)].temperature = {
                last: item,
                current: item,
                invalid: false
              }
            } else {
              resultBack[Math.floor(index / 2)].value = {
                last: item,
                current: item,
                invalid: false
              }
            }
          })
          return resultBack
        }
      },
      expHeatDependentValues: function (param, exp) {
        param.forEach((item, index) => {
          if (index % 2 === 1) {
            param[index] = Number(item * 10 ** exp)
          }
        })
        return param
      },
      validateNestedJob: function () {
        let valid = true
        valid = this.verifyString(this.nestedJobName, 2) &&
          this.verifyNumPositive(this.power) &&
          this.verifyNumPositive(this.nlaser) &&
          this.verifyNumPositive(this.vlaserSupp) &&
          this.verifyNumPositive(this.hatch) &&
          this.verifyNumPositive(this.CrashTolerance) &&
          this.verifyNumPositive(this.fragmentationSize) &&
          this.verifyNumPositive(this.tetraEps) &&
          this.verifyNumPositive(this.tetraMeshSize) &&
          this.verifyNumPositive(this.diameter) &&
          this.verifyNumPositive(this.thickness) &&
          this.verifyNumPositive(this.speed) &&
          this.verifyNumPositive(this.element)
        this.files.forEach((file, index) => {
          if (this.verifyNumPositive(file.scale) &&
            this.verifyNum(file.hsub) &&
            this.verifyNumPositive(file.suppSpacing_xy) &&
            this.verifyNumPositive(file.suppHatchSize) &&
            this.verifyNumPositive(file.supp_size_boundary) &&
            this.verifyNum(file.critical_angle)) {
            return
          } else {
            valid = false
            this.fileIndex = index
          }
        })
        return valid
      },
      materialChoice: function (index) {
        this.material = index + 1
        this.materialvisible = false
        if (this.material === this.materials.length) {
          this.$modal.show('othermaterialmodal')
        }
      },
      removeMaterial: function (index) {
        const materialName = this.materials[index].name
        this.materialChoice(0)
        store.commit('REMOVE_CUSTOM_MATERIAL', materialName)
        delete this.materialsConfig[materialName]
        const userMaterials = JSON.parse(localStorage.getItem('materials'))
        delete userMaterials[materialName]
        localStorage.setItem('materials', JSON.stringify(userMaterials))
      },
      unitChoice: function (index) {
        this.files[this.fileIndex].unit = index + 1
        this.unitvisible = false
      },
      unitSuppChoice: function (index) {
        this.files[this.fileIndex].unit_supp = index
        this.unitsuppvisible = false
      },
      structureChoice: function (index) {
        this.structure = index + 1
        this.structurevisible = false
      },
      printerChoice: function (index) {
        this.printer = index + 1
        this.printervisible = false
        if (this.printer < this.printers.length) {
          const printerConfig = this.printersConfig[this.printers[index].name]
          this.diameter.current = printerConfig.diameter
          this.power.current = printerConfig.power
          this.speed.current = printerConfig.speed
          this.thickness.current = printerConfig.thickness
          if (this.nestedMode) {
            this.parentJobConfig.plateWidth = this.isCustomPrinter ? printerConfig.PrinterXYZ[0] : printerConfig.plateX
            this.parentJobConfig.plateHeight = this.isCustomPrinter ? printerConfig.PrinterXYZ[1] : printerConfig.plateY
            this.parentJobConfig.plateMode = printerConfig.plateMode
            this.parentJobConfig.plateDiameter = Number(printerConfig.plateDiameter)
            this.parentJobConfig.plateThickness = Number(printerConfig.plateThickness)
            this.parentJobConfig.calibrationFactor = Number(printerConfig.calibrationFactor)
          }
        }
        if (this.printer === this.printers.length) {
          this.printerAdded = false
          this.$modal.show('otherprintermodal')
        }
      },
      removePrinter: function (index) {
        const printerName = this.printers[index].name
        this.printerChoice(0)
        store.commit('REMOVE_CUSTOM_PRINTER', printerName)
        delete this.printersConfig[printerName]
        const userPrinters = JSON.parse(localStorage.getItem('printers'))
        delete userPrinters[printerName]
        localStorage.setItem('printers', JSON.stringify(userPrinters))
      },
      confirmAdvanced: function () {
        this.$modal.show('cautionmodal')
      },
      acceptModal: function (showAdvanced) {
        this.$modal.hide('cautionmodal')
        this.showAdvanced = showAdvanced
      },
      toggleRemesh: function (event) {
        this.files = this.files.map((file) => {
          file.needRemesh = event.target.checked
          return file
        })
      },
      toggleNestedRemesh: function (event, inx) {
        if (!this.partParamsAll) this.files[inx].needRemesh = event.target.checked
        else this.toggleRemesh(event)
      },
      getPrinters: function () {
        let userPrinters = localStorage.getItem('printers')
        if (userPrinters) {
          userPrinters = JSON.parse(userPrinters)
          Object.keys(userPrinters).forEach((key) => {
            store.commit('ADD_CUSTOM_PRINTER', { name: key })
            if (userPrinters[key].plateHeight || userPrinters[key].plateWidth) {
              userPrinters[key] = Object.assign({}, {
                DirectionRecoater: userPrinters[key].DirectionRecoater,
                PrinterXYZ: userPrinters[key].PrinterXYZ,
                diameter: userPrinters[key].diameter,
                plateDiameter: userPrinters[key].PrinterXYZ[1],
                plateMode: userPrinters[key].plateMode,
                plateThickness: userPrinters[key].plateThickness || 30,
                power: userPrinters[key].power,
                speed: userPrinters[key].speed,
                thickness: userPrinters[key].thickness,
                calibrationFactor: userPrinters[key].calibrationFactor || 1
              })
            }
          })
          localStorage.setItem('printers', JSON.stringify(userPrinters))
          Object.assign(this.printersConfig, userPrinters)
        }
      },
      getMaterials: function () {
        let userMaterials = localStorage.getItem('materials')
        if (userMaterials) {
          userMaterials = JSON.parse(userMaterials)
          Object.keys(userMaterials).forEach((key) => {
            store.commit('ADD_CUSTOM_MATERIAL', {
              name: key,
              trademark: false,
              valid: false
            })
          })
          Object.assign(this.materialsConfig, userMaterials)
        }
      },
      addPrinter: function () {
        if (this.verifyPrinterName(this.printerCustom.name) &&
          this.verifyString(this.printerCustom.name, 2) &&
          this.verifyNumPositive(this.printerCustom.plateX) &&
          this.verifyNumPositive(this.printerCustom.plateY) &&
          this.verifyNumPositive(this.printerCustom.plateZ) &&
          this.verifyNumPositive(this.printerCustom.plateThickness) &&
          this.verifyNumPositive(this.printerCustom.diameter) &&
          this.verifyNumPositive(this.printerCustom.power) &&
          this.verifyNumPositive(this.printerCustom.speed) &&
          this.verifyNumPositive(this.printerCustom.thickness) &&
          this.verifyNumBetween(this.printerCustom.calibrationFactor, 0, 2)
        ) {
          let userPrinters = localStorage.getItem('printers')
          if (userPrinters) {
            userPrinters = JSON.parse(userPrinters)
          } else {
            userPrinters = {}
          }
          store.commit('ADD_CUSTOM_PRINTER', { name: this.printerCustom.name.current })
          this.printersConfig[this.printerCustom.name.current] = {
            PrinterXYZ: [
              Number(this.printerCustom.plateX.current),
              Number(this.printerCustom.plateY.current),
              Number(this.printerCustom.plateZ.current)
            ],
            DirectionRecoater: this.printerCustom.DirectionRecoater.current === 'width' ? [-1, 0, 0] : [0, -1, 0],
            diameter: this.printerCustom.diameter.current,
            power: this.printerCustom.power.current,
            speed: this.printerCustom.speed.current,
            thickness: this.printerCustom.thickness.current,
            plateMode: 0,
            plateDiameter: Number(this.printerCustom.plateY.current),
            plateThickness: Number(this.printerCustom.plateThickness.current),
            calibrationFactor: Number(this.printerCustom.calibrationFactor.current)
          }
          userPrinters[this.printerCustom.name.current] = this.printersConfig[this.printerCustom.name.current]
          localStorage.setItem('printers', JSON.stringify(userPrinters))
          this.printerCustom = {
            name: {last: '', current: '', invalid: false, duplicate: false},
            DirectionRecoater: {last: '', current: 'width', invalid: false},
            diameter: {last: '', current: '100', invalid: false},
            power: {last: '', current: '200', invalid: false},
            speed: {last: '', current: '500', invalid: false},
            thickness: {last: '', current: '40', invalid: false},
            plateX: {last: '', current: '250', invalid: false},
            plateY: {last: '', current: '250', invalid: false},
            plateZ: {last: '', current: '250', invalid: false},
            plateThickness: {last: '', current: '30', invalid: false},
            plateMode: {last: '', current: '0', invalid: false},
            plateDiameter: {last: '', current: '250', invalid: false},
            calibrationFactor: {last: '', current: '1', invalid: false}
          }
          this.printerAdded = true
          this.printerChoice(this.printers.length - 2)
          this.$modal.hide('otherprintermodal')
        }
      },
      addMaterial: function (skipValidation) {
        if (skipValidation || (this.verifyMaterialName(this.materialCustom.name) &&
          this.verifyString(this.materialCustom.name, 2) &&
          this.verifyNumPositive(this.materialCustom.nu) &&
          this.verifyNumPositive(this.materialCustom.E) &&
          this.verifyHeatDependentValues(this.materialCustom.E_heat) &&
          this.verifyNumPositive(this.materialCustom.rho) &&
          this.verifyNumPositive(this.materialCustom.Cp) &&
          this.verifyNumPositive(this.materialCustom.k) &&
          this.verifyNumPositive(this.materialCustom.Tsolid) &&
          this.verifyNumPositive(this.materialCustom.Tliquid) &&
          this.verifyNumPositive(this.materialCustom.sigma_y) &&
          this.verifyHeatDependentValues(this.materialCustom.sigma_y_heat) &&
          this.verifyNumPositive(this.materialCustom.hsl) &&
          this.verifyNumPositive(this.materialCustom.alpha))) {
          let userMaterials = localStorage.getItem('materials')
          if (userMaterials) {
            userMaterials = JSON.parse(userMaterials)
          } else {
            userMaterials = {}
          }
          store.commit('ADD_CUSTOM_MATERIAL', {
            name: this.materialCustom.name.current,
            trademark: false,
            valid: false
          })
          this.materialsConfig[this.materialCustom.name.current] = {
            nu: this.materialCustom.nu.current,
            E: this.materialCustom.E.current,
            E_heat: this.parseHeatDependentValues(this.materialCustom.E_heat, true),
            rho: this.materialCustom.rho.current,
            Cp: this.materialCustom.Cp.current,
            k: this.materialCustom.k.current,
            Tsolid: this.materialCustom.Tsolid.current,
            Tliquid: this.materialCustom.Tliquid.current,
            sigma_y: this.materialCustom.sigma_y.current,
            sigma_y_heat: this.parseHeatDependentValues(this.materialCustom.sigma_y_heat, true),
            hsl: this.materialCustom.hsl.current,
            alpha: this.materialCustom.alpha.current
          }
          userMaterials[this.materialCustom.name.current] = this.materialsConfig[this.materialCustom.name.current]
          localStorage.setItem('materials', JSON.stringify(userMaterials))
          this.materialCustom = {
            name: {last: '', current: '', invalid: false, duplicate: false},
            nu: {last: '', current: '', invalid: false},
            E: {last: '', current: '', invalid: false},
            E_heat: [{
              temperature: {last: '', current: '', invalid: false},
              value: {last: '', current: '', invalid: false}
            }],
            rho: {last: '', current: '', invalid: false},
            Cp: {last: '', current: '', invalid: false},
            k: {last: '', current: '', invalid: false},
            Tsolid: {last: '', current: '', invalid: false},
            Tliquid: {last: '', current: '', invalid: false},
            sigma_y: {last: '', current: '', invalid: false},
            sigma_y_heat: [{
              temperature: {last: '', current: '', invalid: false},
              value: {last: '', current: '', invalid: false}
            }],
            hsl: {last: '', current: '', invalid: false},
            alpha: {last: '', current: '', invalid: false}
          }
          this.materialAdded = true
          this.$modal.hide('othermaterialmodal')
        }
      },
      addMaterialHeatDependentValue: function (value) {
        value.push({
          temperature: {last: '', current: '', invalid: false},
          value: {last: '', current: '', invalid: false}
        })
      },
      removeMaterialHeatDependentValue: function (value, index) {
        value.splice(index, 1)
      },
      beforeClosePrinter: function () {
        if (this.printerAdded) {
          this.printer = this.printers.length - 1
        } else {
          this.printer = 1
        }
      },
      beforeCloseMaterial: function () {
        if (this.materialAdded) {
          this.material = this.materials.length - 1
        } else {
          this.material = 1
        }
      },
      restoreJob: function (job) {
        this.restorePrinterConfig(job.config)
        this.printer = this.printers.findIndex((printer) => {
          return printer.name === job.config.printer
        }) + 1
        this.printerChoice(this.printer - 1)
        this.restoreMaterialConfig(job.config)
        if (job.config.material_name) {
          this.material = this.materials.findIndex((material) => {
            return material.name === job.config.material_name
          }) + 1
        } else {
          this.material = job.config.material[0]
        }
        this.restoreJobConfig(job.config)
      },
      restorePrinterConfig: function (config) {
        const printerIndex = this.printers.findIndex((printer) => {
          return printer.name === config.printer
        }) + 1
        if (printerIndex > 0) {
          this.speed.current = config.Speed_laser[0]
          this.diameter.current = config.Diameter_laser[0]
          this.power.current = config.Power_laser[0]
          this.thickness.current = config.layerThickness[0]
        } else {
          this.printerCustom.name.current = config.printer
          this.printerCustom.plateX.current = config.PrinterXYZ[0]
          this.printerCustom.plateY.current = config.PrinterXYZ[1]
          this.printerCustom.plateZ.current = config.PrinterXYZ[2]
          this.printerCustom.plateThickness = (config.plateThickness && config.plateThickness[0]) || 30
          this.printerCustom.DirectionRecoater.current = config.DirectionRecoater && config.DirectionRecoater[0] ? 'width' : 'length'
          this.printerCustom.diameter.current = config.Diameter_laser[0]
          this.printerCustom.power.current = config.Power_laser[0]
          this.printerCustom.speed.current = config.Speed_laser[0]
          this.printerCustom.thickness.current = config.layerThickness[0]
          this.printerCustom.calibrationFactor.current = (config.calibrationFactor && config.calibrationFactor[0]) || 1
          this.addPrinter()
        }
      },
      restoreMaterialConfig: function (config) {
        const materialIndex = this.materials.findIndex((material) => {
          return material.name === config.material_name
        }) + 1
        if (materialIndex === 0 && config.material_name) {
          this.materialCustom.name.current = config.material_name
          this.materialCustom.nu.current = config.nu[0]
          this.materialCustom.E.current = config.E[0]
          this.materialCustom.E_heat = this.parseHeatDependentValues(config.E_heat, false)
          this.materialCustom.rho.current = config.rho[0]
          this.materialCustom.Cp.current = config.Cp[0]
          this.materialCustom.k.current = config.k[0]
          this.materialCustom.Tsolid.current = config.Tsolid[0]
          this.materialCustom.Tliquid.current = config.Tliquid[0]
          this.materialCustom.sigma_y.current = config.sigma_y[0]
          this.materialCustom.sigma_y_heat = this.parseHeatDependentValues(config.sigma_y_heat, false)
          this.materialCustom.hsl.current = Number(config.hsl[0] * 10 ** -3)
          this.materialCustom.alpha.current = Number(config.alpha[0] * 10 ** 6)
          this.addMaterial(true)
        }
      },
      restoreJobConfig: function (config, def) {
        this.defaultFileConfig.scale.current = config.scaling[0]
        this.defaultFileConfig.unit = this.units.findIndex((unit) => {
          return unit.name === config.unit
        }) + 1
        this.element.current = config.elementsize[0] * 1000
        this.element.last = config.elementsize[0] * 1000
        this.defaultFileConfig.critical_angle.current = config.critical_angle[0]
        this.defaultFileConfig.hsub.current = config.hsub[0]
        this.defaultFileConfig.suppSpacing_xy.current = config.suppSpacing_xy[0]
        this.defaultFileConfig.suppHatchSize.current = config.suppHatchSize[0]
        this.defaultFileConfig.supp_size_boundary.current = config.supp_size_boundary[0]

        this.speed.current = config.Speed_laser
        this.diameter.current = config.Diameter_laser
        this.power.current = config.Power_laser
        this.thickness.current = config.layerThickness

        // Restore support structure values
        if (config.Wt && config.Wt[0]) {
          this.supportParams.wt.current = config.Wt[0] * 100
        }
        if (config.Pt && config.Pt[0]) {
          this.supportParams.pt.current = config.Pt[0]
        }
        if (config.Tt && config.Tt[0]) {
          this.supportParams.tt.current = config.Tt[0]
        }
        if (config.Ot && config.Ot[0]) {
          this.supportParams.ot.current = config.Ot[0]
        }
        if (config.Wb && config.Wb[0]) {
          this.supportParams.wb.current = config.Wb[0] * 100
        }
        if (config.Pb && config.Pb[0]) {
          this.supportParams.pb.current = config.Pb[0]
        }
        if (config.Tb && config.Tb[0]) {
          this.supportParams.tb.current = config.Tb[0]
        }
        if (config.Ob && config.Ob[0]) {
          this.supportParams.ob.current = config.Ob[0]
        }
        if (config.fraction_avg && config.fraction_avg[0]) {
          this.supportParams.fraction_avg.current = config.fraction_avg[0]
        }
        if (config.Tplate && config.Tplate[0]) {
          this.tplate.current = config.Tplate[0]
        }
        if (config.isCompensate && config.isCompensate.length > 0) {
          this.defaultFileConfig.isCompensate = (config.isCompensate[0] > 0)
        }
        if (config.filename_supp && config.filename_supp.length > 0) {
          this.fileSupports = config.filename_supp.reduce((acc, curr) => {
            acc[curr] = true
            return acc
          }, {})
          this.defaultFileConfig.filename_supp = config.filename_supp
          this.defaultFileConfig.unit_supp = this.units_supp.findIndex((unit) => {
            return unit.name === config.unit_supp
          })
        }

        if (config.scalingX_material && config.scalingX_material.length > 0) {
          this.scalingX_material.current = config.scalingX_material[0]
        }
        if (config.scalingY_material && config.scalingY_material.length > 0) {
          this.scalingY_material.current = config.scalingY_material[0]
        }

        if (config.NLaser && config.NLaser[0]) {
          this.nlaser.current = config.NLaser[0]
        }

        if (config.VlaserSupp && config.VlaserSupp[0]) {
          this.vlaserSupp.current = config.VlaserSupp[0]
        }

        if (config.hatch && config.hatch[0]) {
          this.hatch.current = config.hatch[0]
        }

        if (config.hasOwnProperty('isRepair')) {
          this.isRepair = config.isRepair
        }
        if (config.hasOwnProperty('isStrengthSupp')) {
          this.isStrengthSupp = !!config.isStrengthSupp[0]
        }
        if (config.hasOwnProperty('isEvalFacet')) {
          this.isEvalFacet = config.isEvalFacet
        }

        if (config.hasOwnProperty('isEvalVertex')) {
          this.isEvalVertex = config.isEvalVertex
        }

        if (config.hasOwnProperty('isEvalEdge')) {
          this.isEvalEdge = config.isEvalEdge
        }

        if (config.hasOwnProperty('isFragmentation')) {
          this.isFragmentation = config.isFragmentation
        }

        if (config.H_buildPlate && config.H_buildPlate.length > 0) {
          this.H_buildPlate.current = config.H_buildPlate[0]
        }

        if (config.CrashTolerance && config.CrashTolerance.length > 0) {
          this.CrashTolerance.current = config.CrashTolerance[0]
        }

        if (config.fragmentationSize && config.fragmentationSize.length > 0) {
          this.fragmentationSize.current = config.fragmentationSize[0]
        }

        if (config.compensationScale && config.compensationScale.length > 0) {
          this.compensationScale.current = config.compensationScale[0]
        }

        if (config.tetraEps && config.tetraEps.length > 0) {
          this.tetraEps.current = config.tetraEps[0]
        }
        if (config.tetraMeshSize && config.tetraMeshSize.length > 0) {
          this.tetraMeshSize.current = config.tetraMeshSize[0]
        }

        this.isWithoutSupport = config.isSupport && !config.isSupport[0]
      },
      restoreJobSpecification: function (specification) {
        this.saveSpec = true
        if (specification.printer >= this.printers.length) {
          specification.printer = 1
        }
        if (specification.material >= this.materials.length) {
          specification.material = 1
        }
        this.printer = specification.printer
        this.material = specification.material
        this.speed.current = specification.speed
        this.diameter.current = specification.diameter
        this.power.current = specification.power
        this.thickness.current = specification.thickness
        this.element.current = specification.element
        this.defaultFileConfig.scale.current = specification.scale
        this.defaultFileConfig.hsub.current = specification.hsub
        this.defaultFileConfig.suppSpacing_xy.current = specification.suppSpacing_xy
        this.defaultFileConfig.suppHatchSize.current = specification.suppHatchSize
        this.defaultFileConfig.supp_size_boundary.current = specification.supp_size_boundary
        this.defaultFileConfig.critical_angle.current = specification.critical_angle
        this.defaultFileConfig.unit = specification.unit
        if (specification.wt) {
          this.supportParams.wt.current = specification.wt
        }
        if (specification.pt) {
          this.supportParams.pt.current = specification.pt
        }
        if (specification.tt) {
          this.supportParams.tt.current = specification.tt
        }
        if (specification.ot) {
          this.supportParams.ot.current = specification.ot
        }
        if (specification.wb) {
          this.supportParams.wb.current = specification.wb
        }
        if (specification.pb) {
          this.supportParams.pb.current = specification.pb
        }
        if (specification.tb) {
          this.supportParams.tb.current = specification.tb
        }
        if (specification.ob) {
          this.supportParams.ob.current = specification.ob
        }
        if (specification.fraction_avg) {
          this.supportParams.fraction_avg.current = specification.fraction_avg
        }
        if (specification.tplate) {
          this.tplate.current = specification.tplate
        }
        if (specification.hasOwnProperty('isCompensate')) {
          this.defaultFileConfig.isCompensate = false // specification.isCompensate
        }
        if (specification.nlaser) {
          this.nlaser.current = specification.nlaser
        }
        if (specification.vlaserSupp) {
          this.vlaserSupp.current = specification.vlaserSupp
        }
        if (specification.hatch) {
          this.hatch.current = specification.hatch
        }

        if (specification.hasOwnProperty('isRepair')) {
          this.isRepair = specification.isRepair
        }

        if (specification.hasOwnProperty('isEvalFacet')) {
          this.isEvalFacet = specification.isEvalFacet
        }
        if (specification.hasOwnProperty('isStrengthSupp')) {
          this.isStrengthSupp = !!specification.isStrengthSupp[0]
        }
        if (specification.hasOwnProperty('isEvalVertex')) {
          this.isEvalVertex = specification.isEvalVertex
        }

        if (specification.hasOwnProperty('isEvalEdge')) {
          this.isEvalEdge = specification.isEvalEdge
        }

        if (specification.hasOwnProperty('isFragmentation')) {
          this.isFragmentation = specification.isFragmentation
        }

        if (specification.hasOwnProperty('H_buildPlate')) {
          this.H_buildPlate.current = specification.H_buildPlate
        }

        if (specification.hasOwnProperty('scalingX_material')) {
          this.scalingX_material.current = specification.scalingX_material
        }

        if (specification.hasOwnProperty('scalingY_material')) {
          this.scalingY_material.current = specification.scalingY_material
        }

        if (specification.hasOwnProperty('CrashTolerance')) {
          this.CrashTolerance.current = specification.CrashTolerance
        }
        if (specification.hasOwnProperty('fragmentationSize')) {
          this.fragmentationSize.current = specification.fragmentationSize
        }
        if (specification.hasOwnProperty('tetraEps')) {
          this.tetraEps.current = specification.tetraEps
        }
        if (specification.hasOwnProperty('compensationScale')) {
          this.compensationScale = specification.compensationScale
        }
        if (specification.hasOwnProperty('tetraMeshSize')) {
          this.tetraMeshSize.current = specification.tetraMeshSize
        }
      },
      filesPrepare: function (keys) {
        const files = []
        keys.forEach((key) => {
          if (this.parentPathKey) {
            const defaultFileConfig = {
              scale: {last: '', current: this.filesNested[key].config.scaling[0], invalid: false},
              hsub: {last: '', current: this.filesNested[key].config.hsub[0], invalid: false},
              suppSpacing_xy: {last: '', current: this.filesNested[key].config.suppSpacing_xy[0], invalid: false},
              suppHatchSize: {last: '', current: this.filesNested[key].config.suppHatchSize[0], invalid: false},
              supp_size_boundary: {last: '', current: this.filesNested[key].config.supp_size_boundary[0], invalid: false},
              critical_angle: {last: '', current: this.filesNested[key].config.critical_angle[0], invalid: false},
              unit: this.units.findIndex((unit) => {
                return unit.name === this.filesNested[key].config.unit
              }) + 1,
              isCompensate: this.filesNested[key].config.isCompensate[0]
            }
            files.push(
              JSON.parse(JSON.stringify(Object.assign({}, this.filesNested[key], defaultFileConfig)))
            )
            this.partParamsAll = false
          } else {
            files.push(
              JSON.parse(JSON.stringify(Object.assign({}, this.filesNested[key], this.defaultFileConfig)))
            )
          }
        })
        return files
      },
      switchFile: function (index) {
        this.fileIndex = index
      },
      switchPartParamAll: function () {
        this.switchFile(0)
        if (!this.partParamsAll) {
          this.copyPartParamAll()
        }
      },
      copyPartParamAll: function () {
        this.files.forEach((file) => {
          file.scale.current = this.files[0].scale.current
          file.hsub.current = this.files[0].hsub.current
          file.suppSpacing_xy.current = this.files[0].suppSpacing_xy.current
          file.suppHatchSize.current = this.files[0].suppHatchSize.current
          file.supp_size_boundary.current = this.files[0].supp_size_boundary.current
          file.critical_angle.current = this.files[0].critical_angle.current
          file.isCompensate = this.files[0].isCompensate
          file.unit = this.files[0].unit
          file.needRemesh = this.files[0].needRemesh
        })
      },
      checkNestedMode: function () {
        return this.$route.name === 'NestedCreate' || this.$route.name === 'NestedCreateById'
      },
      getNestedJobsFromState: function () {
        if (Object.keys(this.filesNested).length > 0) {
          this.files = this.filesPrepare(Object.keys(this.filesNested))
          this.switchFile(0)
        } else {
          this.$router.replace({name: 'Dash'})
        }
      },
      getNestedJobsRestore: function () {
        return new Promise((resolve, reject) => {
          return this.getJob(this.parentPathKey)
            .then((response) => {
              if (response && response.data.Job && response.data.Job.hasOwnProperty('nestedJobName')) {
                this.nestedJobName.current = response.data.Job.nestedJobName
                return this.getNestedJobs(this.parentPathKey)
              } else {
                this.$router.push({name: 'Dash'})
              }
            })
            .then((response) => {
              if (response && response.data.allJobs && response.data.allJobs.length > 0) {
                this.setNestedFileData(response.data.allJobs)
                resolve(response.data.allJobs)
              } else {
                reject()
              }
            })
            .catch((err) => {
              reject(err)
            })
        })
      },
      getMaterailConfig: function () {
        return !this.isCustomMaterial ? {
          'material': this.material,
          'material_name': this.materials[this.material - 1].name,
          'nu': [],
          'E': [],
          'E_heat': [],
          'rho': [],
          'Cp': [],
          'k': [],
          'Tsolid': [],
          'Tliquid': [],
          'sigma_y': [],
          'sigma_y_heat': [],
          'hsl': [],
          'alpha': []
        } : {
          'material': this.material,
          'material_name': this.materials[this.material - 1].name,
          'nu': [Number(this.materialsConfig[this.materials[this.material - 1].name].nu)],
          'E': [(Number(this.materialsConfig[this.materials[this.material - 1].name].E))],
          'E_heat': this.materialsConfig[this.materials[this.material - 1].name].E_heat,
          'rho': [Number(this.materialsConfig[this.materials[this.material - 1].name].rho)],
          'Cp': [Number(this.materialsConfig[this.materials[this.material - 1].name].Cp)],
          'k': [Number(this.materialsConfig[this.materials[this.material - 1].name].k)],
          'Tsolid': [Number(this.materialsConfig[this.materials[this.material - 1].name].Tsolid)],
          'Tliquid': [Number(this.materialsConfig[this.materials[this.material - 1].name].Tliquid)],
          'sigma_y': [(Number(this.materialsConfig[this.materials[this.material - 1].name].sigma_y))],
          'sigma_y_heat': this.materialsConfig[this.materials[this.material - 1].name].sigma_y_heat,
          'hsl': [(Number(this.materialsConfig[this.materials[this.material - 1].name].hsl * 10 ** 3))],
          'alpha': [(Number(this.materialsConfig[this.materials[this.material - 1].name].alpha * 10 ** -6))]
        }
      },
      goBack: function () {
        if (this.parentPathKey) {
          this.$router.push({name: 'NestedQuantityById', params: {id: this.parentPathKey}})
        } else {
          this.$router.push({name: 'NestedQuantity'})
        }
      },
      changeNestedJobsFlow: function () {
        if (!this.nestedJobFlow.isManually) {
          this.nestedJobFlow.isManualPlacement = false
        }
      },
      goNext: function () {
        if (this.validateNestedJob()) {
          this.submitting = true
          const material = this.getMaterailConfig()
          const nestedJobData = {
            nestedJobs: [],
            nestedJobName: this.nestedJobName.current,
            config: {
              plateWidth: [this.parentJobConfig.plateWidth],
              plateHeight: [this.parentJobConfig.plateHeight],
              plateThickness: [this.parentJobConfig.plateThickness],
              precision: [this.parentJobConfig.precision],
              spacing: [this.parentJobConfig.spacing],
              delta: [this.parentJobConfig.delta],
              deltaR: [Number(this.deltaR.current)],
              manualPlacement: this.nestedJobFlow.isManualPlacement,
              plateMode: [this.parentJobConfig.plateMode],
              plateDiameter: [this.parentJobConfig.plateDiameter],
              Speed_laser: [Number(this.speed.current)],
              VlaserSupp: [Number(this.vlaserSupp.current)],
              hatch: [Number(this.hatch.current)],
              calibrationFactor: [Number(this.parentJobConfig.calibrationFactor) || 1],
              Nlaser: [Number(this.nlaser.current)],
              printer: this.printers[this.printer - 1].name,
              marginX: [Number(this.marginX.current)],
              marginY: [Number(this.marginY.current)],
              ...material
            }
          }
          const remeshPromises = []
          this.files.forEach((file, index) => {
            const needRemesh = (file.needRemesh && !this.partParamsAll) || (this.partParamsAll && this.files[this.fileIndex].needRemesh)
            needRemesh && remeshPromises.push(this.remeshSourceFile(file, index))
          })
          Promise.all(remeshPromises).then((res) => {
            if (this.partParamsAll) {
              this.copyPartParamAll()
            }
            store.commit('SET_NESTED_FILE_NAME', this.nestedJobName.current)
            const currentIndex = this.fileIndex
            this.files.forEach((file, index) => {
              this.fileIndex = index
              store.commit('SET_NESTED_FILE_CONFIG', {
                pathKey: file.pathKey,
                config: this.config
              })
              if (this.parentPathKey) {
                nestedJobData.nestedJobs.push({
                  config: this.config,
                  importance: file.importance,
                  quantity: file.quantity,
                  jobPathKey: file.jobPathKey,
                  targetVersion: this.$store.state.config.TARGET_VERSION
                })
              } else {
                nestedJobData.nestedJobs.push({
                  config: this.config,
                  importance: file.importance,
                  quantity: file.quantity,
                  sourceFilePathKey: file.pathKey,
                  targetVersion: this.$store.state.config.TARGET_VERSION
                })
              }
            })
            this.fileIndex = currentIndex
            const flow = (id) => {
              const {isManually} = this.nestedJobFlow
              const params = {id, type: 'input', createConfig: this.config}
              if (!isManually) {
                return this.$router.push({name: 'NestedMagicById', params})
              }
              this.$router.push({name: 'ThreeJSViewer', params})
            }
            if (this.parentPathKey) {
              nestedJobData.parentPathKey = this.parentPathKey
              this.updateNestedJob(nestedJobData)
                .then((response) => flow(this.parentPathKey))
                .catch((err) => {
                  this.errorHandling(err, () => {
                    this.updateNestedJob(nestedJobData)
                  })
                  this.submitting = false
                })
            } else {
              this.createNestedJob(nestedJobData)
                .then((response) => flow(response.data.jobPathKey))
                .catch((err) => {
                  this.errorHandling(err, () => {
                    this.createNestedJob(nestedJobData)
                  })
                  this.submitting = false
                })
            }
          })
        }
      }
    },
    computed: {
      ...mapState([
        'user',
        'filesNested'
      ]),
      ...mapGetters([
        'jobById',
        'materials',
        'predefinedMaterials',
        'printers',
        'predefinedPrinters'
      ]),
      ...mapGetters({
        subscription: 'user/subscriptionExpiration'
      }),
      config: function () {
        const currentConfig = {
          'N_part': [],
          'filename': this.files[this.fileIndex].name,
          'printer': this.printers[this.printer - 1].name,
          'unit': this.units[this.files[this.fileIndex].unit - 1].name,
          'scaling': [Number(this.files[this.fileIndex].scale.current)],
          'material': [Number(this.material)],
          'RotationalAngle': [Number(this.x), Number(this.y), Number(this.z)],
          'Weights': [0.5, 0.5, 0.5],
          'layerThickness': [Number(this.thickness.current)],
          'elementsize': [Number(this.element.current) / 1000],
          'Power_laser': [Number(this.power.current)],
          'Diameter_laser': [Number(this.diameter.current)],
          'Speed_laser': [Number(this.speed.current)],
          'suppSpacing_xy': [Number(this.files[this.fileIndex].suppSpacing_xy.current)],
          'suppHatchSize': [Number(this.files[this.fileIndex].suppHatchSize.current)],
          'supp_size_boundary': [Number(this.files[this.fileIndex].supp_size_boundary.current)],
          'hsub': [Number(this.files[this.fileIndex].hsub.current)],
          'critical_angle': [Number(this.files[this.fileIndex].critical_angle.current)],
          'Tplate': [Number(this.tplate.current)],
          'scalingX_material': [Number(this.scalingX_material.current)],
          'scalingY_material': [Number(this.scalingY_material.current)],
          'isCompensate': [Number(this.files[this.fileIndex].isCompensate)],
          'isSupport': [Number(!this.isWithoutSupport)],
          'isStrengthSupp': [Number(this.isStrengthSupp)],
          'isHeatTreatment': true,
          'isMinPeeling': false,
          'NLaser': [Number(this.nlaser.current)],
          'VlaserSupp': [Number(this.vlaserSupp.current)],
          'hatch': [Number(this.hatch.current)],
          'MaxDist': [-1],
          'AllowedFaces': [],
          'RestrictedFaces': [],
          'AngleRestriction': [],
          'Wt': [Number(this.supportParams.wt.current) / 100],
          'Pt': [Number(this.supportParams.pt.current)],
          'Tt': [Number(this.supportParams.tt.current)],
          'Ot': [Number(this.supportParams.ot.current)],
          'Wb': [Number(this.supportParams.wb.current) / 100],
          'Pb': [Number(this.supportParams.pb.current)],
          'Tb': [Number(this.supportParams.tb.current)],
          'Ob': [Number(this.supportParams.ob.current)],
          'isRepair': this.isRepair,
          'isEvalFacet': this.isEvalFacet,
          'isEvalVertex': this.isEvalVertex,
          'isEvalEdge': this.isEvalEdge,
          'isFragmentation': this.isFragmentation,
          'H_buildPlate': [Number(this.H_buildPlate.current)],
          'CrashTolerance': [Number(this.CrashTolerance.current)],
          'fragmentationSize': [Number(this.fragmentationSize.current)],
          'tetraEps': [Number(this.tetraEps.current)],
          'tetraMeshSize': [Number(this.tetraMeshSize.current)],
          'fraction_avg': [Number(this.supportParams.fraction_avg.current)],
          'PrinterXYZ': [],
          'DirectionRecoater': [],
          'plateThickness': [],
          'material_name': this.materials[this.material - 1].name,
          'nu': [],
          'E': [],
          'E_heat': [],
          'rho': [],
          'Cp': [],
          'k': [],
          'Tsolid': [],
          'Tliquid': [],
          'sigma_y': [],
          'sigma_y_heat': [],
          'hsl': [],
          'alpha': [],
          'filename_supp': [],
          'needRemesh': this.files[this.fileIndex].needRemesh,
          'isHideSupports': !this.isPreviewSupports,
          'compensationScale': [Number(this.compensationScale.current)]
        }
        if (Object.keys(this.fileSupports).length > 0) {
          currentConfig.Wt = [Number(this.supportParamsDefault.wt) / 100]
          currentConfig.Pt = [Number(this.supportParamsDefault.pt)]
          currentConfig.Tt = [Number(this.supportParamsDefault.tt)]
          currentConfig.Ot = [Number(this.supportParamsDefault.ot)]
          currentConfig.Wb = [Number(this.supportParamsDefault.wb) / 100]
          currentConfig.Pb = [Number(this.supportParamsDefault.pb)]
          currentConfig.Tb = [Number(this.supportParamsDefault.tb)]
          currentConfig.Ob = [Number(this.supportParamsDefault.ob)]
          currentConfig.fraction_avg = [Number(this.supportParamsDefault.fraction_avg)]
        }
        if (this.isCustomPrinter) {
          currentConfig.PrinterXYZ = this.printersConfig[this.printers[this.printer - 1].name].PrinterXYZ
          currentConfig.DirectionRecoater = this.printersConfig[this.printers[this.printer - 1].name].DirectionRecoater
          currentConfig.plateThickness = [Number(this.printersConfig[this.printers[this.printer - 1].name].plateThickness)]
        }
        if (this.isCustomMaterial) {
          currentConfig.nu = [Number(this.materialsConfig[this.materials[this.material - 1].name].nu)]
          currentConfig.E = [(Number(this.materialsConfig[this.materials[this.material - 1].name].E))]
          currentConfig.E_heat = this.materialsConfig[this.materials[this.material - 1].name].E_heat
          currentConfig.rho = [Number(this.materialsConfig[this.materials[this.material - 1].name].rho)]
          currentConfig.Cp = [Number(this.materialsConfig[this.materials[this.material - 1].name].Cp)]
          currentConfig.k = [Number(this.materialsConfig[this.materials[this.material - 1].name].k)]
          currentConfig.Tsolid = [Number(this.materialsConfig[this.materials[this.material - 1].name].Tsolid)]
          currentConfig.Tliquid = [Number(this.materialsConfig[this.materials[this.material - 1].name].Tliquid)]
          currentConfig.sigma_y = [(Number(this.materialsConfig[this.materials[this.material - 1].name].sigma_y))]
          currentConfig.sigma_y_heat = this.materialsConfig[this.materials[this.material - 1].name].sigma_y_heat
          currentConfig.hsl = [(Number(this.materialsConfig[this.materials[this.material - 1].name].hsl * 10 ** 3))]
          currentConfig.alpha = [(Number(this.materialsConfig[this.materials[this.material - 1].name].alpha * 10 ** -6))]
        }
        if (this.isWithoutSupport) {
          currentConfig.hsub = [0]
        }
        if (this.nestedMode) {
          currentConfig.N_part = [this.numberOfParts]
        }
        if (Object.keys(this.fileSupports).length > 0 && !this.isWithoutSupport) {
          currentConfig.filename_supp = Object.keys(this.fileSupports)
          currentConfig.unit_supp = this.units_supp[this.files[0].unit_supp].name
        } else {
          currentConfig.filename_supp = []
          currentConfig.unit_supp = 'mm'
        }
        return currentConfig
      },
      numberOfParts: function () {
        let count = 0
        Object.keys(this.filesNested).forEach((fileKey) => {
          count += Number(this.filesNested[fileKey].quantity)
        })
        return count
      },
      isCustomPrinter: function () {
        return this.printer > this.predefinedPrinters
      },
      isCustomMaterial: function () {
        return this.material > this.predefinedMaterials
      },
      isValidMaterial: function () {
        return this.materials[this.material - 1].valid
      }
    },
    created () {
      this.getPrintConfig()
        .then(({ data }) => {
          store.commit('SET_MATERIALS', data.config.materials)
          store.commit('SET_PRINTERS', data.config.printers)
          this.printersConfig = data.config.printers.reduce((acc, current) => {
            return Object.assign(this.printersConfig, {[current.name]: current})
          }, {})
          this.nestedMode = this.checkNestedMode()
          this.getPrinters()
          this.getMaterials()
          let jobSpecification = localStorage.getItem('jobSpecification')
          if (jobSpecification) {
            jobSpecification = JSON.parse(jobSpecification)
            this.restoreJobSpecification(jobSpecification)
          } else {
            this.printerChoice(0)
          }
          if (this.nestedMode) {
            if (this.$route.params.id) {
              this.parentPathKey = this.$route.params.id
              return this.getNestedJobsRestore()
                .then((jobs) => {
                  this.getNestedJobsFromState()
                  this.restoreJob(jobs[0])
                  this.fileLoading = false
                })
                .catch((err) => {
                  this.errorHandling(err, this.getNestedJobsRestore)
                })
            } else {
              this.getNestedJobsFromState()
              this.fileLoading = false
            }
          } else {
            return store.dispatch('loadSourceFile', { id: this.$route.params.id, forceRefresh: false })
              .then(() => {
                this.file = this.$store.state.files.find((file) => {
                  return file.id === this.$route.params.id
                })
                this.files.push(JSON.parse(JSON.stringify(Object.assign({}, this.file, this.defaultFileConfig))))
                if (this.$route.params.jobPathKey) {
                  this.getJobById({id: this.$route.params.id, jobPathKey: this.$route.params.jobPathKey})
                    .then((job) => {
                      if (job) {
                        this.restoreJob(job)
                        this.files = [JSON.parse(JSON.stringify(Object.assign({}, this.file, this.defaultFileConfig)))]
                      }
                      this.fileLoading = false
                    })
                } else {
                  this.fileLoading = false
                }
              })
              .catch((err) => {
                this.errorHandling(err, () => {
                  store.dispatch('loadSourceFile', { id: this.$route.params.id, forceRefresh: false })
                })
              })
          }
        })
        .catch((err) => {
          this.errorHandling(err, this.getPrintConfig)
        })
    }
  }
</script>

<style lang="scss" scoped>
    .container {
        .a3d.btn {
            white-space: nowrap;
            min-width: 170px;

            &.multiline {
              white-space: normal;
              height: auto;
              line-height: 26px;
            }
        }
        .block-title {
            text-transform: uppercase;
            font-size: 20px;
        }
        .block-button {
            padding-bottom: 16px;
        }
        .error {
            color: #ff0000;
        }
        .row {
            .status-message {
                font-size: 1.6rem;
            }
        }
        .blocks-wrp {
            display: flex;
            flex-wrap: wrap;

            label {
                color: inherit;
            }

            .divider-block {
                flex-basis: 100%;
                padding-top: 20px;
            }

            .block-full-width {
                flex-basis: 100%;
            }

            .block-wrp {
                width: 50%;
                align-self: stretch;
                padding: 16px;

                &.block-wrp-33 {
                    width: 33.33%;
                }

                &.block-wrp-100 {
                    width: 100%;
                }
                &.block-wrp__flex {
                  display: flex;
                  flex-direction: column
                }
                &.with-image {
                  display: flex;
                }

                #uploadsupports {
                    display: none;
                }

                .block-image {
                  width: 200px;
                  min-width: 120px;
                  background-color: $bg-gray;
                  padding: 20px 0 0 10px;

                  img {
                    width: 100%;
                  }
                }

                .block-input.block-input__half {
                    margin-bottom: 10px;

                    &:last-child {
                      margin: 0;
                      height: fit-content;
                    }
                }
                .block-input {
                    width: 100%;
                    height: 100%;
                    background-color: $bg-gray;
                    padding: 24px;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;

                    .upload-supports {
                        padding-bottom: 8px;
                    }

                    .block-support {
                      display: flex;
                      justify-content: space-between;
                      padding-top: 12px;

                      .a3d.btn {
                        width: 150px;
                        min-width: 150px;
                      }
                    }

                    .support-checkboxes {
                      max-width: 300px;
                      display: flex;
                      flex-direction: column;
                      align-self: center;
                    }

                    .block-description {
                        text-align: justify;
                    }

                    .block-alert {
                        margin: 8px 0;
                        padding: 12px;
                        background-color: #fff;
                        text-align: justify;
                    }

                    .block-value, .block-value-range {
                        padding-top: 24px;
                        text-align: left;
                        display: flex;
                        flex-direction: row;

                        .icon-remove {
                            padding-top: 9px;
                            padding-right: 9px;
                        }

                        label:after {
                            background-color: #00BDD1;
                            border-color: #00BDD1;
                        }

                        &.column {
                            flex-direction: column;
                        }

                        input[type="number"], input[type="text"] {
                            box-sizing: border-box;
                            background-color: #fff;
                            padding: 0 12px;
                            margin: 0;
                            flex: 8;
                        }

                        input[type="range"] {
                            flex: 3;
                            border: none;
                        }

                        [type="radio"]:not(:checked) + label,
                        [type="radio"]:checked + label {
                            min-height: 25px;
                            height: auto;
                        }

                        .field-name {
                            flex: 1;
                            font-weight: bold;
                            font-size: 22px;
                            padding-top: 1px;
                            padding-right: 5px;
                            min-width: 36px;

                            &.small {
                                font-size: 16px;
                            }
                        }

                        .unit {
                            flex: 1;
                            padding-left: 16px;
                            font-size: 22px;
                            font-weight: bold;
                            white-space: nowrap;
                            line-height: 42px;

                            &.first {
                                padding-right: 16px;
                            }

                            &.label {
                                padding-left: 0;
                                padding-right: 16px;
                            }
                        }

                        &.select {
                            z-index: 10;

                            .menu {
                                .btn {
                                    height: 52px;
                                    line-height: 36px;
                                    padding: 6px 1rem 0 1rem;
                                }
                            }

                            .select-icon {
                                display: inline-block;
                                padding-right: 12px;

                                img {
                                    max-height: 36px;
                                    width: auto;
                                    border-radius: 50%;
                                }
                            }
                            .select-text {
                                display: inline-block;
                                vertical-align: top;
                            }
                        }
                    }

                    .block-buttons {
                        padding-top: 12px;
                    }
                }
                .block-support-input {
                    input[type=number] {
                        flex: 3;
                    }

                    .block-value-range {
                        align-items: center;

                        .field-name {
                            min-width: 72px;
                        }

                        input[type="number"] {
                            max-width: 72px;
                        }

                        .tooltip-wrp {
                            flex: 3;
                        }
                    }
                }
            }

            .block-checkbox {
                flex-basis: 100%;
                padding-bottom: 12px;
            }

            .block-dots {
                flex-basis: 100%;

                &.top {
                    padding-top: 12px;
                }

                &.bottom {
                    padding-bottom: 12px;
                }

                .tooltip-wrp {
                    display: inline-block;

                    .tooltip {
                        display: none;
                        margin: 0;

                        .tooltipvalue {
                            top: -52px;
                            left: 12px;
                            width: auto;
                            padding: 2px 12px;
                            transform:translateX(-50%);
                            margin-left: 0;
                        }
                    }

                    .file-dot {
                        margin: 4px;
                        width: 16px;
                        height: 16px;
                        border: 1px solid #00BDD1;
                        border-radius: 50%;
                        background-color: transparent;

                        &.selected, &:hover {
                            background-color: #00BDD1;
                        }
                    }

                    .file-dot:hover + .tooltip {
                        display: block;
                    }
                }
            }
        }

        .startjobor {
            .file-name {
                color: $atlas-blue;
                margin-top: 0;
                word-break: break-all;
            }

            .title {
                padding-top: 32px;
                text-transform: uppercase;
            }
        }
        .init-settings {
            padding: 0;
            display: flex;

            .settings-block {
                border: 1px solid $border-color;
                padding: 24px;

                &:last-child {
                    border-left: none;
                }

                .alert {
                    padding-top: 20px;
                    font-size: 0.9em;
                    font-weight: bold;
                }
            }
        }
        .jobSpec {
            padding-top: 20px;
            margin-bottom: 0;

            .col.s12 {
                padding-bottom: 8px;
            }

            label {
                color: inherit;
            }
        }
    }

    a {
        cursor: pointer;
    }

    #uploadfile {
        display: none;
    }

    a.a3d.btn {
        width: 100%;
    }

    .menu-list {
        .material-icons {
            position: absolute;
            left: 4px;
            cursor: pointer;
        }
    }

    .caution-modal {
        padding: 20px;
        .text-block {
            text-align: justify;
            padding-bottom: 20px;
        }
        .row {
            margin-bottom: 0;
        }
    }

    @media (max-width: 992px) {
        .container {
            .init-settings {
                flex-direction: column;
                .settings-block {
                    &:last-child {
                        border-top: none;
                        border-left: 1px solid $border-color;
                    }
                }
            }
        }
    }

    @media (max-width: 768px) {
        .container {
            .a3d.btn {
                min-width: 120px;
            }
            .block-title {
                font-size: 18px;
            }
            .blocks-wrp {
                .block-wrp {
                    flex-basis: 100%;
                }
            }
        }
    }
</style>



// WEBPACK FOOTER //
// Create.vue?43c09b44